import * as XLSX from "xlsx-js-style";
import axios from "api/axios";
import {
  PROGRAM_METADATA_FIELDS,
  getProgramStageEventFields,
} from "pages/tracker-registration/programMetadata";
import * as bulkloadUtils from "utils/bulkload";

const EXPECTED_BULKLOAD_SHEET_NAME = "TEI Instances";
const EXCLUDED_BULKLOAD_HEADERS = new Set(["No geometry", "District Logo"]);
const BULKLOAD_FILE_VALUE_TYPES = new Set(["FILE_RESOURCE", "IMAGE"]);
const DEFAULT_TRACKER_IMPORT_DATETIME = "2025-01-01T00:00:00.000";
const LEGACY_TEMPLATE_HEADER_ROW_INDEX = 4;
const LEGACY_TEMPLATE_SYSTEM_COLUMN_COUNT = 5;
const LEGACY_TEMPLATE_ATTRIBUTE_FILL = "FFCA28";
const TIMELINE_SYSTEM_COLUMN_COUNT = 4;
const TIMELINE_SHEET_NAME_PATTERN = /^\(\d+\)\s/;
const TRACKER_UID_PATTERN = /^[a-zA-Z0-9]{11}$/;
const TIMELINE_HEADER_ROW_MARKERS = new Set([
  "event id",
  "tei id",
  "options",
  "date",
  "reporting date",
  "report date",
]);
const PROJECTS_PROGRAMMES_PROGRAM_ID = "g3wMUKEMmH3";
const PROJECT_ID_ATTRIBUTE_ID = "s9DLL3i1DoG";

const createTrackerBulkloadConfig = (key, trackerNames, mandatoryTrackerKey, buildPayload) => ({
  key,
  trackerNames,
  mandatoryTrackerKey,
  buildPayload,
});

const TRACKER_BULKLOAD_CONFIGS = [
  createTrackerBulkloadConfig(
    "aap-baselines-and-targets",
    ["Annual Action Plan Baselines and Targets", "Plan Baselines and Targets"],
    "Annual Action Plan Baselines and Targets",
    bulkloadUtils.getAAPBaselineAndTargetPayload,
  ),
  createTrackerBulkloadConfig(
    "annual-action-plan",
    ["Annual Action Plan (AAP)", "Annual Action Plan"],
    "Annual Action Plan (AAP)",
    bulkloadUtils.getAAPPayload,
  ),
  createTrackerBulkloadConfig(
    "projects-and-programmes",
    ["Projects & Programmes Register (P&P)", "Projects & Programmes"],
    "Projects & Programmes Register (P&P)",
    bulkloadUtils.getProjectPayload,
  ),
  createTrackerBulkloadConfig("bill-tracker", ["Bill Tracker"], "Bill Tracker", bulkloadUtils.getBillingPayload),
  createTrackerBulkloadConfig(
    "budget-allocation-tracker",
    ["Budget Allocation Tracker"],
    "Budget Allocation Tracker",
    bulkloadUtils.getBudgetPayload,
  ),
  createTrackerBulkloadConfig(
    "district-general-tracker",
    ["District General Tracker"],
    "District General Tracker",
    bulkloadUtils.getGeneralDistrictPayload,
  ),
  createTrackerBulkloadConfig(
    "operational-health-facility-tracker",
    ["Operational Health Facility Tracker", "Operational Health Facility", "Health Facility Tracker"],
    "Operational Health Facility Tracker",
    bulkloadUtils.getOperationalHealthFacilityPayload,
  ),
  createTrackerBulkloadConfig(
    "sport-facility-tracker",
    ["Sport Facility Tracker", "Sport Facility"],
    "Sport Facility Tracker",
    bulkloadUtils.getSportFacilityPayload,
  ),
  createTrackerBulkloadConfig(
    "service-providers-tracker",
    ["Service Providers Tracker", "Service Providers", "Service Provider Tracker"],
    "Service Providers Tracker",
    bulkloadUtils.getServiceProvidersPayload,
  ),
  createTrackerBulkloadConfig(
    "school-profile-tracker",
    ["School Profile Tracker", "School Profile"],
    "School Profile Tracker",
    bulkloadUtils.getSchoolProfilePayload,
  ),
  createTrackerBulkloadConfig(
    "meetings-tracker",
    ["Meetings Tracker", "Meetings"],
    "Meetings Tracker",
    bulkloadUtils.getMeetingsPayload,
  ),
  createTrackerBulkloadConfig(
    "people-with-disability-tracker",
    ["People With Disability Tracker", "People With Disability", "PWD Tracker"],
    "People With Disability Tracker",
    bulkloadUtils.getPeopleWithDisabilityPayload,
  ),
  createTrackerBulkloadConfig(
    "social-inclusive-program-tracker",
    ["Social Inclusive Program Tracker", "Social Inclusive Programme Tracker"],
    "Social Inclusive Program Tracker",
    bulkloadUtils.getSocialInclusiveProgramTrackerPayload,
  ),
  createTrackerBulkloadConfig(
    "social-inclusive-program-beneficiaries-tracker",
    [
      "Social Inclusive Program Beneficiaries Tracker",
      "Social Inclusive Programme Beneficiaries Tracker",
      "Program Beneficiaries Tracker",
    ],
    "Social Inclusive Program Beneficiaries Tracker",
    bulkloadUtils.getSocialInclusiveProgramBeneficiariesTrackerPayload,
  ),
  createTrackerBulkloadConfig(
    "program-tracker",
    ["Program Tracker", "Programme Tracker", "PWD Program Tracker"],
    "Program Tracker",
    bulkloadUtils.getProgramPayload,
  ),
  createTrackerBulkloadConfig(
    "boundary-dispute-tracker",
    ["Boundary Dispute Tracker", "Boundary Dispute"],
    "Boundary Dispute Tracker",
    bulkloadUtils.getBoundaryDisputePayload,
  ),
  createTrackerBulkloadConfig(
    "computerized-billing-system",
    ["Computerized Billing System"],
    "Computerized Billing System",
    bulkloadUtils.getComputerizedBillingSystemPayload,
  ),
  createTrackerBulkloadConfig(
    "capacity-building-needs",
    ["Capacity Building Needs"],
    "Capacity Building Needs",
    bulkloadUtils.getCapacityBuildingNeedsPayload,
  ),
  createTrackerBulkloadConfig(
    "consulting-and-contractors",
    ["Consulting And Contractors", "Consulting and Contractors"],
    "Consulting And Contractors",
    bulkloadUtils.getConsultingAndContractorsPayload,
  ),
  createTrackerBulkloadConfig(
    "decisions-and-complaints",
    ["Decisions And Complaints", "Decisions and Complaints"],
    "Decisions And Complaints",
    bulkloadUtils.getDecisionsAndComplaintsPayload,
  ),
  createTrackerBulkloadConfig(
    "disaster-tracker",
    ["Disaster Tracker", "Disaster"],
    "Disaster Tracker",
    bulkloadUtils.getDisasterPayload,
  ),
  createTrackerBulkloadConfig(
    "district-assembly-departments",
    ["District Assembly Departments"],
    "District Assembly Departments",
    bulkloadUtils.getDistrictAssemblyDepartmentsPayload,
  ),
  createTrackerBulkloadConfig(
    "expenditure-tracker",
    ["Expenditure Tracker", "Expenditure"],
    "Expenditure Tracker",
    bulkloadUtils.getExpenditurePayload,
  ),
  createTrackerBulkloadConfig(
    "job-tracker",
    ["Job Tracker", "Jobs Tracker"],
    "Job Tracker",
    bulkloadUtils.getJobPayload,
  ),
  createTrackerBulkloadConfig(
    "food-vendors-register",
    ["Food Vendors Register", "Food Vendor Register"],
    "Food Vendors Register",
    bulkloadUtils.getFoodVendorsRegisterPayload,
  ),
  createTrackerBulkloadConfig(
    "evaluation-and-pme-tracker",
    ["Evaluation And PME Tracker", "Evaluation and PME Tracker", "PME Tracker"],
    "Evaluation And PME Tracker",
    bulkloadUtils.getEvaluationAndPMEPayload,
  ),
  createTrackerBulkloadConfig(
    "dumping-site-or-engineered-landfills",
    ["Dumping Site or engineered landfills", "Dumping Site or Engineered Landfills", "Engineered Landfills"],
    "Dumping Site or engineered landfills",
    bulkloadUtils.getDumpingSiteOrEngineeredLandfillsPayload,
  ),
  createTrackerBulkloadConfig(
    "dpat-scoring-sheet",
    ["DPAT Scoring Sheet"],
    "DPAT Scoring Sheet",
    bulkloadUtils.getDPATScoringSheetPayload,
  ),
  createTrackerBulkloadConfig(
    "inspectorate-unit-activity-tracker",
    ["Inspectorate Unit Activity Tracker"],
    "Inspectorate Unit Activity Tracker",
    bulkloadUtils.getInspectorateUnitActivityPayload,
  ),
  createTrackerBulkloadConfig(
    "inspectorate-unit-establishment-tracker",
    ["Inspectorate Unit Establishment Tracker"],
    "Inspectorate Unit Establishment Tracker",
    bulkloadUtils.getInspectorateUnitEstablishmentTrackerPayload,
  ),
  createTrackerBulkloadConfig(
    "internally-generated-fund-tracker",
    ["Internally Generated Fund Tracker", "IGF Tracker"],
    "Internally Generated Fund Tracker",
    bulkloadUtils.getInternallyGeneratedFundTrackerPayload,
  ),
  createTrackerBulkloadConfig(
    "key-critical-poverty-issues-tracker",
    ["Key Critical Poverty Issues Tracker", "Critical Poverty Issues Tracker"],
    "Key Critical Poverty Issues Tracker",
    bulkloadUtils.getKeyCriticalPovertyIssuesPayload,
  ),
  createTrackerBulkloadConfig(
    "permits-request-tracker",
    ["Permits Request Tracker", "Permit Request Tracker"],
    "Permits Request Tracker",
    bulkloadUtils.getPermitsRequestPayload,
  ),
  createTrackerBulkloadConfig(
    "street-naming-tracker",
    ["Street Naming Tracker", "Street Naming"],
    "Street Naming Tracker",
    bulkloadUtils.getStreetNamingPayload,
  ),
  createTrackerBulkloadConfig(
    "statutory-member-tracker",
    ["Statutory Member Tracker", "Statutory Members Tracker"],
    "Statutory Member Tracker",
    bulkloadUtils.getStatutoryMemberPayload,
  ),
  createTrackerBulkloadConfig(
    "logistic-tracker",
    ["Logistic Tracker", "Logistics Tracker"],
    "Logistic Tracker",
    bulkloadUtils.getLogisticPayload,
  ),
  createTrackerBulkloadConfig(
    "vulnerability-tracker",
    ["Vulnerability Tracker"],
    "Vulnerability Tracker",
    bulkloadUtils.getVulnerabilityPayload,
  ),
  createTrackerBulkloadConfig(
    "legal-case-tracker",
    ["Legal Case Tracker", "Legal Cases Tracker"],
    "Legal Case Tracker",
    bulkloadUtils.getLegalCasePayload,
  ),
  createTrackerBulkloadConfig(
    "led-related-programs",
    ["LED Related Programs", "LED Related Programme"],
    "LED Related Programs",
    bulkloadUtils.getLEDRelatedProgramsPayload,
  ),
  createTrackerBulkloadConfig(
    "property-tracker",
    ["Property Tracker", "Properties Tracker"],
    "Property Tracker",
    bulkloadUtils.getPropertyTrackerPayload,
  ),
  createTrackerBulkloadConfig(
    "marketing-business-tracker",
    ["Marketing Business Tracker", "Marketing Business"],
    "Marketing Business Tracker",
    bulkloadUtils.getMarketingBusinessTrackerPayload,
  ),
  createTrackerBulkloadConfig(
    "payment-point-tracker",
    ["Payment Point Tracker", "Payment Point"],
    "Payment Point Tracker",
    bulkloadUtils.getPaymentPointPayload,
  ),
  createTrackerBulkloadConfig(
    "audit-issues-tracker",
    ["Audit Issues Tracker", "Audit Issues"],
    "Audit Issues Tracker",
    bulkloadUtils.getAuditIssuesPayload,
  ),
  createTrackerBulkloadConfig(
    "annual-progress-report-tracker",
    ["Annual Progress Report Tracker", "Annual Progress Report"],
    "Annual Progress Report Tracker",
    bulkloadUtils.getAnnualProgressReportPayload,
  ),
  createTrackerBulkloadConfig(
    "revenue-tracker",
    ["Revenue Tracker", "Revenue"],
    "Revenue Tracker",
    bulkloadUtils.getRevenuePayload,
  ),
  createTrackerBulkloadConfig(
    "publications-tracker",
    ["Publications Tracker", "Publication Tracker"],
    "Publications Tracker",
    bulkloadUtils.getPublicationsPayload,
  ),
  createTrackerBulkloadConfig(
    "sanitation-and-waste-tracker",
    ["Sanitation and Waste Tracker", "Sanitation & Waste Tracker"],
    "Sanitation and Waste Tracker",
    bulkloadUtils.getSanitationAndWasteTrackerPayload,
  ),
  createTrackerBulkloadConfig(
    "sub-structure-activity-tracker",
    ["Sub Structure Activity Tracker", "Sub-Structure Activity Tracker"],
    "Sub Structure Activity Tracker",
    bulkloadUtils.getSubStructureActivityTrackerPayload,
  ),
  createTrackerBulkloadConfig(
    "sub-structure-committee",
    ["Sub Structure Committee", "Sub-Structure Committee"],
    "Sub Structure Committee",
    bulkloadUtils.getSubStructureCommitteePayload,
  ),
  createTrackerBulkloadConfig(
    "test-fruit-application",
    ["Test Fruit Application"],
    "Test Fruit Application",
    bulkloadUtils.getTestFruitApplicationPayload,
  ),
  createTrackerBulkloadConfig(
    "residential-home-centre",
    ["Residential Home Centre"],
    "Residential Home Centre",
    bulkloadUtils.getResidentialHomeCentrePayload,
  ),
  createTrackerBulkloadConfig("user-profile", ["User Profile"], "User Profile", bulkloadUtils.getUserProfilePayload),
  createTrackerBulkloadConfig("documents-hub", ["Documents Hub"], "Documents Hub", bulkloadUtils.getDocumentsHubPayload),
  createTrackerBulkloadConfig(
    "one-district-one-warehouse-tracker",
    ["One District One Warehouse Tracker", "1D1W Tracker"],
    "One District One Warehouse Tracker",
    bulkloadUtils.getOneDistrictOneWarehouseTrackerPayload,
  ),
  createTrackerBulkloadConfig(
    "community-profile",
    ["Community Profile"],
    "Community Profile",
    bulkloadUtils.getCommunityProfilePayload,
  ),
  createTrackerBulkloadConfig(
    "client-service-unit-tracker",
    ["Client Service Unit Tracker", "Client Service Unit"],
    "Client Service Unit Tracker",
    bulkloadUtils.getClientServiceUnitTrackerPayload,
  ),
  createTrackerBulkloadConfig(
    "atsheet-training-attendance-sheet-tracker",
    ["ATSheet Training Attendance Sheet Tracker", "Training Attendance Sheet Tracker"],
    "ATSheet Training Attendance Sheet Tracker",
    bulkloadUtils.getATSheetTrainingAttendanceSheetTrackerPayload,
  ),
  createTrackerBulkloadConfig(
    "agricultural-extension-and-support-officers",
    ["Agricultural Extension and Support Officers", "Agricultural Extension Officers"],
    "Agricultural Extension and Support Officers",
    bulkloadUtils.getAgriculturalExtensionandSupportOfficersPayload,
  ),
];

export function formatTrackerBulkloadFileSize(sizeInBytes) {
  const size = Number(sizeInBytes) || 0;

  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function cleanBulkloadHeader(value) {
  if (value && typeof value === "object") {
    // ExcelJS may store formula cells as objects; prefer cached result then fall back.
    if (value.result != null && value.result !== "") {
      value = value.result;
    } else if (value.text != null) {
      value = value.text;
    } else if (value.richText) {
      value = (value.richText || []).map((part) => part?.text || "").join("");
    } else if (value.formula != null) {
      value = "";
    }
  }

  const cleaned = String(value ?? "")
    .replace(/\r?\n/g, " ")
    .replace(/\(.*?\)/g, "")
    .replace(/\*/g, "")
    .replace(/\s+/g, " ")
    .trim();

  // Ignore Excel error tokens that previously appeared in formula-driven headers.
  if (/^#(VALUE!|REF!|NAME\?|N\/A|DIV\/0!|NULL!|NUM!|GETTING_DATA)$/i.test(cleaned)) {
    return "";
  }

  return cleaned;
}

function isBulkloadFileOrImageValueType(valueType) {
  return BULKLOAD_FILE_VALUE_TYPES.has(String(valueType || "").toUpperCase());
}

function isBulkloadFileOrImageField(field) {
  if (!field) return false;
  if (field.type === "file") return true;
  return isBulkloadFileOrImageValueType(field.valueType);
}

function isBulkloadExcludedAttributeEntry(entry, columnConfig) {
  const attribute = entry?.trackedEntityAttribute || entry;
  if (!attribute) return true;

  if (isBulkloadFileOrImageValueType(attribute.valueType)) {
    return true;
  }

  const label = cleanBulkloadHeader(getPreferredBulkloadAttributeLabel(entry, columnConfig));
  return EXCLUDED_BULKLOAD_HEADERS.has(label);
}

function resolveBulkloadDisplayLabel(...candidates) {
  for (const candidate of candidates) {
    const cleaned = cleanBulkloadHeader(candidate);
    if (cleaned) return cleaned;
  }
  return "";
}

function normalizeBulkloadCellValue(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  return value;
}

function hasVisibleBulkloadCellValue(value) {
  return normalizeBulkloadCellValue(value) !== "-";
}

function normalizeBulkloadRows(rows) {
  return (rows || []).filter(
    (row) => Array.isArray(row) && row.some((cell) => hasVisibleBulkloadCellValue(cell)),
  );
}

function isValidTrackerUid(value) {
  return TRACKER_UID_PATTERN.test(String(value || "").trim());
}

function isTimelineHeaderRow(row) {
  const eventCell = normalizeTrackerName(cleanBulkloadHeader(row?.[0]));
  const teiCell = normalizeTrackerName(cleanBulkloadHeader(row?.[1]));
  return TIMELINE_HEADER_ROW_MARKERS.has(eventCell) || TIMELINE_HEADER_ROW_MARKERS.has(teiCell);
}

function isTimelineUpdateRow(row) {
  const eventId = normalizeBulkloadCellValue(row?.[0]);
  return isValidTrackerUid(eventId);
}

function extractTimelineDataElementColumns(worksheet) {
  return extractBulkloadHeaderAttributeColumns(worksheet).map(({ attributeId, columnIndex }) => ({
    dataElementId: attributeId,
    columnIndex,
  }));
}

function getTimelineFieldColumns(sheet, programStage) {
  if (Array.isArray(sheet?.dataElementColumns) && sheet.dataElementColumns.length > 0) {
    return sheet.dataElementColumns;
  }

  return getTimelineStageFields(programStage).map((field, fieldIndex) => ({
    dataElementId: field.name,
    columnIndex: TIMELINE_SYSTEM_COLUMN_COUNT + fieldIndex,
  }));
}

function parseTimelineWorksheet(worksheet) {
  const matrixRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "-" });

  if (!Array.isArray(matrixRows) || matrixRows.length < 2) {
    return null;
  }

  const candidates = [
    buildMatrixParseCandidate(
      matrixRows,
      LEGACY_TEMPLATE_HEADER_ROW_INDEX,
      LEGACY_TEMPLATE_HEADER_ROW_INDEX + 1,
    ),
    buildMatrixParseCandidate(matrixRows, 3, 4),
    buildMatrixParseCandidate(matrixRows, 2, 3),
    buildMatrixParseCandidate(matrixRows, 1, 2),
    buildMatrixParseCandidate(matrixRows, 0, 1),
  ].filter(Boolean);

  if (candidates.length === 0) {
    return null;
  }

  const parsedSheet = candidates.sort((left, right) => right.score - left.score)[0];
  const rows = parsedSheet.rows.filter((row) => !isTimelineHeaderRow(row));

  if (rows.length === 0) {
    return null;
  }

  return {
    ...parsedSheet,
    rows,
    score: parsedSheet.headers.length * 10 + rows.length,
  };
}

function buildBulkloadColumnsFromHeaderRow(headerRow) {
  return Object.entries(headerRow || {})
    .map(([key, value]) => {
      const label = cleanBulkloadHeader(value);

      if (!label || EXCLUDED_BULKLOAD_HEADERS.has(label)) {
        return null;
      }

      return { key, label };
    })
    .filter(Boolean);
}

function parseWorksheetFromObjectRows(worksheet) {
  const rawRows = XLSX.utils.sheet_to_json(worksheet, { defval: "-" });

  if (!Array.isArray(rawRows) || rawRows.length < 2) {
    return null;
  }

  const columns = buildBulkloadColumnsFromHeaderRow(rawRows[0]);

  if (columns.length === 0) {
    return null;
  }

  const rows = normalizeBulkloadRows(
    rawRows.slice(1).map((row) => columns.map(({ key }) => normalizeBulkloadCellValue(row?.[key]))),
  );

  if (rows.length === 0) {
    return null;
  }

  return {
    headers: columns.map((column) => column.label),
    rows,
    score: columns.length * 10 + rows.length,
  };
}

function buildMatrixParseCandidate(matrixRows, headerRowIndex, dataStartIndex) {
  const headerRow = Array.isArray(matrixRows?.[headerRowIndex]) ? matrixRows[headerRowIndex] : [];
  const columnCount = Math.max(
    headerRow.length,
    ...matrixRows
      .slice(dataStartIndex)
      .map((row) => (Array.isArray(row) ? row.length : 0)),
  );

  const visibleColumns = headerRow
    .map((value, index) => {
      const label = cleanBulkloadHeader(value);

      if (!label) {
        return null;
      }

      return {
        index,
        label,
        excluded: EXCLUDED_BULKLOAD_HEADERS.has(label),
      };
    })
    .filter(Boolean);

  if (visibleColumns.length === 0) {
    return null;
  }

  const rows = normalizeBulkloadRows(
    matrixRows
      .slice(dataStartIndex)
      .map((row) => {
        const fullRow = [];
        for (let index = 0; index < columnCount; index += 1) {
          fullRow[index] = normalizeBulkloadCellValue(row?.[index]);
        }
        return fullRow;
      }),
  );

  if (rows.length === 0) {
    return null;
  }

  return {
    headers: visibleColumns.filter((column) => !column.excluded).map((column) => column.label),
    previewHeaders: visibleColumns.map((column) => column.label),
    previewColumns: visibleColumns
      .filter((column) => !column.excluded)
      .map((column) => ({
        header: column.label,
        columnIndex: column.index,
      })),
    columnCount,
    rows,
    score: visibleColumns.length * 10 + rows.length,
  };
}

function parseWorksheetFromMatrixRows(worksheet) {
  const matrixRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "-" });

  if (!Array.isArray(matrixRows) || matrixRows.length < 2) {
    return null;
  }

  const candidates = [
    buildMatrixParseCandidate(
      matrixRows,
      LEGACY_TEMPLATE_HEADER_ROW_INDEX,
      LEGACY_TEMPLATE_HEADER_ROW_INDEX + 1,
    ),
    buildMatrixParseCandidate(matrixRows, 3, 4),
    buildMatrixParseCandidate(matrixRows, 1, 2),
    buildMatrixParseCandidate(matrixRows, 0, 1),
  ].filter(Boolean);

  if (candidates.length === 0) {
    return null;
  }

  return candidates.sort((left, right) => right.score - left.score)[0];
}

function extractBulkloadHeaderAttributeColumns(worksheet) {
  const formulaCellsByRow = new Map();

  Object.entries(worksheet || {}).forEach(([cellReference, cell]) => {
    if (cellReference.startsWith("!") || typeof cell?.f !== "string") return;

    const formulaMatch = cell.f.trim().match(/^_([A-Za-z0-9]{11})$/);
    if (!formulaMatch) return;

    const position = XLSX.utils.decode_cell(cellReference);
    const rowCells = formulaCellsByRow.get(position.r) || [];
    rowCells.push({
      columnIndex: position.c,
      attributeId: formulaMatch[1],
      label: cleanBulkloadHeader(cell?.v),
    });
    formulaCellsByRow.set(position.r, rowCells);
  });

  const preferredRowCells = formulaCellsByRow.get(LEGACY_TEMPLATE_HEADER_ROW_INDEX);
  const headerCells =
    preferredRowCells?.length > 0
      ? preferredRowCells
      : Array.from(formulaCellsByRow.values()).sort(
    (left, right) => right.length - left.length,
  )[0];

  return (headerCells || [])
    .sort((left, right) => left.columnIndex - right.columnIndex)
    .filter((cell) => !EXCLUDED_BULKLOAD_HEADERS.has(cell.label))
    .map((cell) => ({
      attributeId: cell.attributeId,
      columnIndex: cell.columnIndex,
    }));
}

function extractBulkloadHeaderAttributeIds(worksheet) {
  return extractBulkloadHeaderAttributeColumns(worksheet).map((cell) => cell.attributeId);
}

function resolveBulkloadSystemColumnIndexes(headerRow) {
  const labels = (headerRow || []).map((value) => normalizeTrackerName(cleanBulkloadHeader(value)));

  const findIndex = (...needles) => {
    const index = labels.findIndex((label) =>
      needles.some((needle) => label.includes(normalizeTrackerName(needle))),
    );
    return index >= 0 ? index : null;
  };

  return {
    teiId: findIndex("tei id") ?? 0,
    orgUnit: findIndex("org unit") ?? 1,
    enrollmentDate: findIndex("enrollment date") ?? 3,
    incidentDate: findIndex("incident date") ?? 4,
  };
}

function parseTeiInstancesWorksheet(worksheet) {
  const matrixRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "-" });

  if (!Array.isArray(matrixRows) || matrixRows.length <= LEGACY_TEMPLATE_HEADER_ROW_INDEX + 1) {
    return null;
  }

  let parsedSheet = buildMatrixParseCandidate(
    matrixRows,
    LEGACY_TEMPLATE_HEADER_ROW_INDEX,
    LEGACY_TEMPLATE_HEADER_ROW_INDEX + 1,
  );

  if (!parsedSheet?.rows?.length) {
    const fallbackCandidates = [
      buildMatrixParseCandidate(matrixRows, 3, 4),
      buildMatrixParseCandidate(matrixRows, 1, 2),
      buildMatrixParseCandidate(matrixRows, 0, 1),
    ].filter(Boolean);

    parsedSheet = fallbackCandidates.sort((left, right) => right.score - left.score)[0] || null;
  }

  if (!parsedSheet?.rows?.length) {
    return null;
  }

  const headerRow = Array.isArray(matrixRows[LEGACY_TEMPLATE_HEADER_ROW_INDEX])
    ? matrixRows[LEGACY_TEMPLATE_HEADER_ROW_INDEX]
    : [];

  return {
    ...parsedSheet,
    systemColumnIndexes: resolveBulkloadSystemColumnIndexes(headerRow),
  };
}

export function getBulkloadPreviewColumns(parsedWorkbook) {
  if (Array.isArray(parsedWorkbook?.previewColumns) && parsedWorkbook.previewColumns.length) {
    return parsedWorkbook.previewColumns;
  }

  const headers = parsedWorkbook?.previewHeaders || parsedWorkbook?.headers || [];

  return headers.map((header, columnIndex) => ({
    header,
    columnIndex,
  }));
}

export async function parseTrackerBulkloadWorkbook(file) {
  if (!(file instanceof File)) {
    throw new Error("Choose an Excel file before continuing.");
  }

  if (!/\.(xlsx|xls)$/i.test(file.name)) {
    throw new Error("Upload an Excel file in .xlsx or .xls format.");
  }

  const fileBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(fileBuffer, { type: "array" });
  const worksheetName =
    workbook?.SheetNames?.find((name) => name === EXPECTED_BULKLOAD_SHEET_NAME) ||
    workbook?.SheetNames?.[0];

  if (!worksheetName) {
    throw new Error("This workbook does not contain any sheets to import.");
  }

  const worksheet = workbook?.Sheets?.[worksheetName];

  if (!worksheet) {
    throw new Error("The selected worksheet could not be read.");
  }

  const parsedSheet = parseTeiInstancesWorksheet(worksheet);

  if (!parsedSheet?.rows?.length || !parsedSheet?.headers?.length) {
    throw new Error("The workbook could not be parsed. Please use the tracker bulkload template.");
  }

  const timelineSheets = (workbook?.SheetNames || [])
    .filter((name) => TIMELINE_SHEET_NAME_PATTERN.test(name))
    .map((name) => {
      const timelineWorksheet = workbook?.Sheets?.[name];
      const parsedTimelineSheet = parseTimelineWorksheet(timelineWorksheet);

      if (!parsedTimelineSheet?.rows?.length || !parsedTimelineSheet?.headers?.length) {
        return null;
      }

      return {
        sheetName: name,
        headers: parsedTimelineSheet.headers,
        rows: parsedTimelineSheet.rows,
        rowCount: parsedTimelineSheet.rows.length,
        columnCount: parsedTimelineSheet.headers.length,
        dataElementColumns: extractTimelineDataElementColumns(timelineWorksheet),
      };
    })
    .filter(Boolean);

  return {
    fileName: file.name,
    fileSize: file.size,
    fileSizeLabel: formatTrackerBulkloadFileSize(file.size),
    headers: parsedSheet.headers,
    previewHeaders: parsedSheet.previewHeaders || parsedSheet.headers,
    previewColumns: parsedSheet.previewColumns,
    attributeIds: extractBulkloadHeaderAttributeIds(worksheet),
    attributeColumns: extractBulkloadHeaderAttributeColumns(worksheet),
    systemColumnIndexes: parsedSheet.systemColumnIndexes,
    rows: parsedSheet.rows,
    rowCount: parsedSheet.rows.length,
    columnCount: parsedSheet.columnCount || parsedSheet.previewHeaders?.length || parsedSheet.headers.length,
    sheetName: worksheetName,
    matchesExpectedSheet: worksheetName === EXPECTED_BULKLOAD_SHEET_NAME,
    timelineSheets,
    timelineSheetCount: timelineSheets.length,
  };
}

function normalizeTrackerName(value) {
  return String(value || "").trim().toLowerCase();
}

function formatDatePart(value, length = 2) {
  return String(value).padStart(length, "0");
}

function formatTrackerImportDateTime(date) {
  return [
    `${date.getFullYear()}-${formatDatePart(date.getMonth() + 1)}-${formatDatePart(date.getDate())}`,
    `${formatDatePart(date.getHours())}:${formatDatePart(date.getMinutes())}:${formatDatePart(date.getSeconds())}.${formatDatePart(date.getMilliseconds(), 3)}`,
  ].join("T");
}

function normalizeExcelSerialDate(value) {
  const parsed = XLSX.SSF.parse_date_code(value);

  if (!parsed) {
    return null;
  }

  const wholeSeconds = Math.floor(parsed.S || 0);
  const milliseconds = Math.round(((parsed.S || 0) - wholeSeconds) * 1000);

  return `${formatDatePart(parsed.y, 4)}-${formatDatePart(parsed.m)}-${formatDatePart(parsed.d)}T${formatDatePart(
    parsed.H || 0,
  )}:${formatDatePart(parsed.M || 0)}:${formatDatePart(wholeSeconds)}.${formatDatePart(milliseconds, 3)}`;
}

function normalizeTrackerImportDate(value, fallbackValue = DEFAULT_TRACKER_IMPORT_DATETIME) {
  if (value === null || value === undefined || value === "" || value === "-") {
    return fallbackValue;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    if (value > 1000000000000) {
      return formatTrackerImportDateTime(new Date(value));
    }

    const excelDate = normalizeExcelSerialDate(value);
    return excelDate || fallbackValue;
  }

  const stringValue = String(value).trim();

  if (!stringValue || stringValue === "-") {
    return fallbackValue;
  }

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}$/.test(stringValue)) {
    return stringValue;
  }

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(stringValue)) {
    return `${stringValue}.000`;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
    return `${stringValue}T00:00:00.000`;
  }

  if (/^\d{13}$/.test(stringValue)) {
    return formatTrackerImportDateTime(new Date(Number(stringValue)));
  }

  if (/^\d{5}$/.test(stringValue)) {
    const excelDate = normalizeExcelSerialDate(Number(stringValue));
    return excelDate || fallbackValue;
  }

  const parsedDate = new Date(stringValue);

  if (!Number.isNaN(parsedDate.getTime())) {
    return formatTrackerImportDateTime(parsedDate);
  }

  return fallbackValue;
}

function normalizeTrackerImportPayload(payload) {
  const trackedEntities = Array.isArray(payload?.trackedEntities) ? payload.trackedEntities : [];

  return {
    ...payload,
    trackedEntities: trackedEntities.map((trackedEntity) => {
      const enrollments = Array.isArray(trackedEntity?.enrollments) ? trackedEntity.enrollments : [];

      return {
        ...trackedEntity,
        enrollments: enrollments.map((enrollment) => {
          const enrolledAt = normalizeTrackerImportDate(enrollment?.enrolledAt);
          const occurredAt = normalizeTrackerImportDate(enrollment?.occurredAt, enrolledAt);

          return {
            ...enrollment,
            enrolledAt,
            occurredAt,
          };
        }),
      };
    }),
  };
}

function getTrackerProgramMetadata(selectedTrackerOrProgram) {
  return selectedTrackerOrProgram?.program || selectedTrackerOrProgram || null;
}

function getTrackerAttributeEntries(programMetadata) {
  const programAttributes = Array.isArray(programMetadata?.programTrackedEntityAttributes)
    ? programMetadata.programTrackedEntityAttributes
    : [];
  const typeAttributes = Array.isArray(programMetadata?.trackedEntityType?.trackedEntityTypeAttributes)
    ? programMetadata.trackedEntityType.trackedEntityTypeAttributes
    : [];

  return [...programAttributes, ...typeAttributes];
}

function generateLocalTrackerAttributeValue(attribute, programMetadata) {
  const programId = String(programMetadata?.id || "").trim();
  const attributeId = String(attribute?.id || "").trim();

  if (programId === PROJECTS_PROGRAMMES_PROGRAM_ID && attributeId === PROJECT_ID_ATTRIBUTE_ID) {
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const randomLetters = Array.from(
      { length: 3 },
      () => letters[Math.floor(Math.random() * letters.length)],
    ).join("");
    const randomNumbers = Math.floor(100000 + Math.random() * 900000);

    return `${randomLetters}${randomNumbers}`;
  }

  return "";
}

function getGeneratedTrackerAttributes(selectedTrackerOrProgram) {
  const programMetadata = getTrackerProgramMetadata(selectedTrackerOrProgram);
  const attributeById = new Map();

  getTrackerAttributeEntries(programMetadata).forEach((entry) => {
    const attribute = entry?.trackedEntityAttribute;
    if (attribute?.generated === true && attribute?.id) {
      attributeById.set(attribute.id, attribute);
    }
  });

  if (String(programMetadata?.id || "").trim() === PROJECTS_PROGRAMMES_PROGRAM_ID) {
    const projectIdAttribute = getTrackerAttributeEntries(programMetadata)
      .map((entry) => entry?.trackedEntityAttribute)
      .find((attribute) => attribute?.id === PROJECT_ID_ATTRIBUTE_ID);

    if (projectIdAttribute) {
      attributeById.set(PROJECT_ID_ATTRIBUTE_ID, projectIdAttribute);
    }
  }

  return Array.from(attributeById.values());
}

function getGeneratedTrackerAttributeIds(selectedTrackerOrProgram) {
  return new Set(getGeneratedTrackerAttributes(selectedTrackerOrProgram).map((attribute) => attribute.id));
}

function removeGeneratedTrackerAttributes(attributes, generatedAttributeIds) {
  if (!Array.isArray(attributes) || generatedAttributeIds.size === 0) {
    return attributes;
  }

  return attributes.filter((attribute) => !generatedAttributeIds.has(attribute?.attribute));
}

function stripGeneratedTrackerAttributes(payload, selectedTracker) {
  const generatedAttributeIds = getGeneratedTrackerAttributeIds(selectedTracker);
  if (generatedAttributeIds.size === 0) {
    return payload;
  }

  return {
    ...payload,
    trackedEntities: (payload?.trackedEntities || []).map((trackedEntity) => ({
      ...trackedEntity,
      attributes: removeGeneratedTrackerAttributes(trackedEntity?.attributes, generatedAttributeIds),
      enrollments: Array.isArray(trackedEntity?.enrollments)
        ? trackedEntity.enrollments.map((enrollment) => ({
            ...enrollment,
            attributes: removeGeneratedTrackerAttributes(enrollment?.attributes, generatedAttributeIds),
          }))
        : trackedEntity?.enrollments,
    })),
  };
}

async function generateTrackerAttributeValue(attribute, programMetadata) {
  const localValue = generateLocalTrackerAttributeValue(attribute, programMetadata);
  if (localValue) {
    return localValue;
  }

  const attributeId = attribute?.id;
  const response = await axios.get(`/trackedEntityAttributes/${attributeId}/generate`);
  const generatedValue = response?.data?.value;

  if (!generatedValue) {
    throw new Error("A generated tracker value was requested, but the server did not return one.");
  }

  return generatedValue;
}

export async function addGeneratedTrackerAttributeValues(payload, selectedTrackerOrProgram) {
  const programMetadata = getTrackerProgramMetadata(selectedTrackerOrProgram);
  const generatedAttributes = getGeneratedTrackerAttributes(selectedTrackerOrProgram);
  if (generatedAttributes.length === 0) {
    return payload;
  }

  const generatedAttributeIds = new Set(generatedAttributes.map((attribute) => attribute.id));
  const trackedEntities = Array.isArray(payload?.trackedEntities) ? payload.trackedEntities : [];

  return {
    ...payload,
    trackedEntities: await Promise.all(
      trackedEntities.map(async (trackedEntity) => {
        const generatedValues = await Promise.all(
          generatedAttributes.map(async (attribute) => ({
            attribute: attribute.id,
            value: await generateTrackerAttributeValue(attribute, programMetadata),
          })),
        );

        return {
          ...trackedEntity,
          attributes: [
            ...removeGeneratedTrackerAttributes(trackedEntity?.attributes, generatedAttributeIds),
            ...generatedValues,
          ],
          enrollments: Array.isArray(trackedEntity?.enrollments)
            ? trackedEntity.enrollments.map((enrollment) => ({
                ...enrollment,
                attributes: [
                  ...removeGeneratedTrackerAttributes(enrollment?.attributes, generatedAttributeIds),
                  ...generatedValues.map((attribute) => ({ ...attribute })),
                ],
              }))
            : trackedEntity?.enrollments,
        };
      }),
    ),
  };
}

function getTrackerBulkloadRequestError(error) {
  const responseMessage =
    error?.response?.data?.message ||
    error?.response?.data?.description ||
    error?.response?.data?.response?.message;
  const rawMessage = responseMessage || error?.message;

  if (typeof rawMessage === "string" && rawMessage.trim()) {
    return rawMessage.split("\n")[0].trim();
  }

  return "Bulkload could not be completed. Please try again.";
}

function getLegacyTrackerTemplateAttributes(selectedTracker, columnConfig) {
  const programMetadata = selectedTracker?.program;
  const mandatoryAttributeIds = getMandatoryBulkloadAttributeIds(programMetadata);

  return (programMetadata?.programTrackedEntityAttributes || [])
    .slice()
    .sort((left, right) => (left?.sortOrder ?? 0) - (right?.sortOrder ?? 0))
    .filter((entry) => entry?.trackedEntityAttribute?.id)
    .filter((entry) => !isBulkloadExcludedAttributeEntry(entry, columnConfig))
    .map((entry) => {
      const attributeId = entry?.trackedEntityAttribute?.id;
      if (!attributeId || entry?.mandatory || !mandatoryAttributeIds.has(attributeId)) {
        return entry;
      }
      return {
        ...entry,
        mandatory: true,
      };
    });
}

function getPreferredBulkloadAttributeLabel(entry, columnConfig) {
  const attribute = entry?.trackedEntityAttribute || entry;
  const savedLabel = attribute?.id ? columnConfig?.[attribute.id]?.displayName : "";

  return (
    resolveBulkloadDisplayLabel(
      savedLabel,
      attribute?.displayFormName,
      attribute?.formName,
      attribute?.displayName,
      attribute?.shortName,
      attribute?.name,
    ) ||
    attribute?.id ||
    ""
  );
}

function getLegacyTrackerTemplateHeaders(selectedTracker, columnConfig) {
  const attributes = getLegacyTrackerTemplateAttributes(selectedTracker, columnConfig);
  const systemHeaders = [
    "TEI id",
    "Org Unit *",
    "No geometry",
    "Enrollment Date *\n(YYYY-MM-DD)",
    "Incident Date\n(YYYY-MM-DD)",
  ];
  const attributeHeaders = attributes.map((entry) => {
    const attribute = entry.trackedEntityAttribute;
    const requiredMarker = entry?.mandatory ? " *" : "";
    const dateHint = attribute?.valueType === "DATE" ? "\n(YYYY-MM-DD)" : "";
    const label = getPreferredBulkloadAttributeLabel(entry, columnConfig);

    return `${label}${requiredMarker}${dateHint}`;
  });

  return [...systemHeaders, ...attributeHeaders];
}

function bulkloadHeadersLooselyMatch(leftHeader, rightHeader) {
  const left = normalizeTrackerName(cleanBulkloadHeader(leftHeader));
  const right = normalizeTrackerName(cleanBulkloadHeader(rightHeader));

  if (!left || !right) return false;
  if (left === right) return true;

  return left.includes(right) || right.includes(left);
}

function validateSystemBulkloadHeaders(headers) {
  const normalized = (headers || []).map((header) =>
    normalizeTrackerName(cleanBulkloadHeader(header)),
  );

  return (
    normalized.some((header) => header.includes("tei")) &&
    normalized.some((header) => header.includes("org unit")) &&
    normalized.some((header) => header.includes("enrollment")) &&
    normalized.some((header) => header.includes("incident"))
  );
}

function validateTrackerAttributeIds(uploadedIds, expectedAttributeIds) {
  if (!Array.isArray(uploadedIds) || uploadedIds.length === 0) {
    return false;
  }

  if (!Array.isArray(expectedAttributeIds) || expectedAttributeIds.length === 0) {
    return false;
  }

  const expectedSet = new Set(expectedAttributeIds);

  return (
    uploadedIds.length === expectedAttributeIds.length &&
    uploadedIds.every((attributeId) => expectedSet.has(attributeId))
  );
}

function validateTrackerBulkloadHeaders(
  headers,
  attributeIds,
  selectedTracker,
  columnConfig,
  attributeColumns = [],
) {
  const actualHeaders = (headers || []).map(cleanBulkloadHeader);
  const expectedHeaders = getLegacyTrackerTemplateHeaders(selectedTracker, columnConfig)
    .map(cleanBulkloadHeader)
    .filter((header) => !EXCLUDED_BULKLOAD_HEADERS.has(header));
  const expectedAttributeIds = getLegacyTrackerTemplateAttributes(selectedTracker, columnConfig)
    .map((entry) => entry.trackedEntityAttribute.id)
    .filter(Boolean);
  const uploadedAttributeIds =
    attributeColumns.length > 0
      ? attributeColumns.map((column) => column.attributeId)
      : attributeIds;
  const hasEmbeddedAttributeIds =
    Array.isArray(uploadedAttributeIds) && uploadedAttributeIds.length > 0;

  if (
    hasEmbeddedAttributeIds &&
    validateTrackerAttributeIds(uploadedAttributeIds, expectedAttributeIds) &&
    validateSystemBulkloadHeaders(actualHeaders)
  ) {
    return;
  }

  const labelsMatch =
    actualHeaders.length === expectedHeaders.length &&
    actualHeaders.every((header, index) =>
      bulkloadHeadersLooselyMatch(header, expectedHeaders[index]),
    );

  if (labelsMatch && validateSystemBulkloadHeaders(actualHeaders)) {
    return;
  }

  const systemHeaderCount = 4;
  const structuralAttributeMatch =
    actualHeaders.length === expectedHeaders.length &&
    actualHeaders
      .slice(systemHeaderCount)
      .every((header, index) =>
        bulkloadHeadersLooselyMatch(header, expectedHeaders[systemHeaderCount + index]),
      );

  if (structuralAttributeMatch && validateSystemBulkloadHeaders(actualHeaders)) {
    return;
  }

    throw new Error(
      `This file does not match the ${selectedTracker?.label || "selected tracker"} template. Download a fresh template and keep its columns unchanged.`,
    );
}

function createLegacyHeaderStyle(fillColor) {
  return {
    fill: {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: fillColor },
    },
    font: {
      bold: true,
      color: { argb: "FF000000" },
    },
    alignment: {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    },
    border: {
      top: { style: "thin", color: { argb: "FF000000" } },
      right: { style: "thin", color: { argb: "FF000000" } },
      bottom: { style: "thin", color: { argb: "FF000000" } },
      left: { style: "thin", color: { argb: "FF000000" } },
    },
  };
}

function configureLegacyWorksheetShell(worksheet) {
  worksheet.getCell("A1").value = " ";
  worksheet.getRow(1).hidden = true;
  worksheet.getRow(2).hidden = true;
  worksheet.getRow(3).hidden = true;
  worksheet.views = [{ state: "frozen", ySplit: 5 }];
}

function applyLegacyWorksheetHeaders(worksheet, headers, systemColumnCount) {
  worksheet.getRow(5).values = headers;
  worksheet.getRow(5).height = 45;

  headers.forEach((header, columnIndex) => {
    const isAttributeColumn = columnIndex >= systemColumnCount;
    const cell = worksheet.getCell(LEGACY_TEMPLATE_HEADER_ROW_INDEX + 1, columnIndex + 1);
    const style = createLegacyHeaderStyle(
      isAttributeColumn ? `FF${LEGACY_TEMPLATE_ATTRIBUTE_FILL}` : "FFFFFFFF",
    );
    cell.fill = style.fill;
    cell.font = style.font;
    cell.alignment = style.alignment;
    cell.border = style.border;
    worksheet.getColumn(columnIndex + 1).width = Math.min(
      Math.max(cleanBulkloadHeader(header).length + 4, 16),
      42,
    );
  });
}

function getSortedProgramStages(programMetadata) {
  return (Array.isArray(programMetadata?.programStages) ? programMetadata.programStages : [])
    .filter((stage) => stage?.id)
    .slice()
    .sort((left, right) => (left?.sortOrder ?? 0) - (right?.sortOrder ?? 0));
}

function sanitizeExcelWorksheetName(rawName, fallback = "Sheet") {
  const cleaned = String(rawName || "")
    .replace(/[*?:\\/\[\]]+/g, "-")
    .replace(/\s+/g, " ")
    .trim();
  const base = cleaned || fallback;
  // Excel worksheet names are limited to 31 characters.
  return base.length > 31 ? base.slice(0, 31).trim() : base;
}

function buildTimelineSheetName(stageIndex, stage, usedNames = null) {
  const stageLabel = stage?.displayName || stage?.name || stage?.id || "Stage";
  const preferred = sanitizeExcelWorksheetName(`(${stageIndex + 1}) ${stageLabel}`, `Stage ${stageIndex + 1}`);
  if (!usedNames) {
    return preferred;
  }

  let candidate = preferred;
  let suffix = 2;
  while (usedNames.has(normalizeTrackerName(candidate))) {
    const suffixText = `-${suffix}`;
    const truncatedBase = preferred.slice(0, Math.max(1, 31 - suffixText.length)).trim();
    candidate = sanitizeExcelWorksheetName(`${truncatedBase}${suffixText}`, `Stage ${stageIndex + 1}`);
    suffix += 1;
  }
  usedNames.add(normalizeTrackerName(candidate));
  return candidate;
}

function buildTimelineSheetsFromStages(stages) {
  const usedNames = new Set();
  return (stages || []).map((stage, stageIndex) => ({
    name: buildTimelineSheetName(stageIndex, stage, usedNames),
    stage,
    headers: getTimelineSheetHeaders(stage),
    rows: [],
  }));
}

function getTimelineSystemDateHeader() {
  // System event occurredAt — shown as Report Date in the tracker UI.
  return "Reporting Date *\n(YYYY-MM-DD)";
}

function isPlannedActivityReportStage(programStage) {
  const stageLabel = normalizeTrackerName(
    programStage?.displayName || programStage?.name || programStage?.id || "",
  );
  return stageLabel.includes("planned activity report");
}

function getTimelineFieldHeaderLabel(field, programStage) {
  const rawLabel =
    resolveBulkloadDisplayLabel(field?.label, field?.name) ||
    String(field?.name || "Field").trim();
  const normalizedLabel = normalizeTrackerName(rawLabel);
  const isDateField =
    String(field?.valueType || "").toUpperCase() === "DATE" ||
    String(field?.valueType || "").toUpperCase() === "DATETIME";

  // AAP Planned Activity Report: avoid a second generic "Date" next to Reporting Date.
  if (isPlannedActivityReportStage(programStage) && isDateField) {
    if (
      !normalizedLabel ||
      normalizedLabel === "date" ||
      normalizedLabel === "event date" ||
      normalizedLabel === "incident date" ||
      normalizedLabel === "event" ||
      normalizedLabel === "occurred at"
    ) {
      return "Incident Date";
    }
  }

  // Any stage: a bare "Date" DE conflicts with the system reporting date column.
  if (isDateField && (!normalizedLabel || normalizedLabel === "date")) {
    return "Event Date";
  }

  return rawLabel;
}

function getTimelineSheetHeaders(programStage) {
  const systemHeaders = [
    "Event id",
    "TEI Id",
    "Options",
    getTimelineSystemDateHeader(),
  ];
  const fieldHeaders = getTimelineStageFields(programStage).map((field) => {
    const requiredMarker = field.required ? " *" : "";
    const dateHint =
      field.valueType === "DATE" || field.valueType === "DATETIME"
        ? "\n(YYYY-MM-DD)"
        : "";
    const label = getTimelineFieldHeaderLabel(field, programStage);
    return `${label}${requiredMarker}${dateHint}`;
  });

  return [...systemHeaders, ...fieldHeaders];
}

function buildEmptyTimelineSheetsFromProgram(programMetadata) {
  return buildTimelineSheetsFromStages(getSortedProgramStages(programMetadata));
}

async function resolveProgramMetadataForBulkload(selectedTracker) {
  const programId = selectedTracker?.value || selectedTracker?.program?.id;
  let program = selectedTracker?.program;

  if (!programId) {
    return program;
  }

  const hasStageElements = (program?.programStages || []).some(
    (stage) =>
      Array.isArray(stage?.programStageDataElements) && stage.programStageDataElements.length > 0,
  );

  if (hasStageElements) {
    return program;
  }

  try {
    const response = await axios.get(`/programs/${programId}`, {
      params: { fields: PROGRAM_METADATA_FIELDS },
    });
    return response?.data || program;
  } catch {
    return program;
  }
}

function resolveProgramStageFromSheetName(sheetName, programMetadata) {
  const match = String(sheetName || "").match(/^\((\d+)\)\s*(.+)$/);
  if (!match) {
    return null;
  }

  const stageIndex = Number.parseInt(match[1], 10) - 1;
  const stageLabel = match[2].trim();
  const stages = getSortedProgramStages(programMetadata);
  const stageByIndex = stages[stageIndex];
  const normalizedSheetLabel = normalizeTrackerName(stageLabel);
  const normalizedSanitizedSheetLabel = normalizeTrackerName(
    sanitizeExcelWorksheetName(stageLabel, stageLabel),
  );

  const labelsMatch = (stage) => {
    const raw = stage?.displayName || stage?.name || "";
    const normalizedRaw = normalizeTrackerName(raw);
    const normalizedSanitized = normalizeTrackerName(sanitizeExcelWorksheetName(raw, raw));
    return (
      normalizedRaw === normalizedSheetLabel ||
      normalizedSanitized === normalizedSheetLabel ||
      normalizedRaw === normalizedSanitizedSheetLabel ||
      normalizedSanitized === normalizedSanitizedSheetLabel
    );
  };

  if (stageByIndex && labelsMatch(stageByIndex)) {
    return stageByIndex;
  }

  return stages.find((stage) => labelsMatch(stage)) || stageByIndex || null;
}

function getDataElementId(dataValueOrId) {
  if (!dataValueOrId) return "";
  if (typeof dataValueOrId === "string") return dataValueOrId.trim();

  const nested = dataValueOrId?.dataElement;
  if (typeof nested === "string") return nested.trim();
  if (nested && typeof nested === "object") {
    return String(nested.id || nested.uid || "").trim();
  }

  return String(dataValueOrId.id || dataValueOrId.uid || "").trim();
}

function buildDataElementLabelMap(programMetadata, columnConfig = {}) {
  const labelMap = new Map();

  getSortedProgramStages(programMetadata).forEach((stage) => {
    getTimelineStageFields(stage).forEach((field) => {
      labelMap.set(field.name, field.label);
    });
  });

  getLegacyTrackerTemplateAttributes({ program: programMetadata }).forEach((entry) => {
    const attribute = entry?.trackedEntityAttribute;
    if (!attribute?.id) return;

    labelMap.set(
      attribute.id,
      getPreferredBulkloadAttributeLabel(entry, columnConfig) ||
        attribute.displayFormName ||
        attribute.displayName ||
        attribute.shortName ||
        attribute.id,
    );
  });

  return labelMap;
}

function getTimelineSheetColumnLabel(programStage, fieldIndex) {
  const header = getTimelineSheetHeaders(programStage)[TIMELINE_SYSTEM_COLUMN_COUNT + fieldIndex];
  return cleanBulkloadHeader(header).replace(/\s*\*$/, "").split("\n")[0].trim();
}

function formatDataElementReference(dataElementId, labelMap) {
  const label = labelMap?.get?.(dataElementId);
  return label ? `${label} (${dataElementId})` : dataElementId;
}

function formatTrackerValidationMessage(message, labelMap) {
  if (!message) return message;

  let formatted = String(message);

  formatted = formatted.replace(
    /DataElement [`']?([a-zA-Z0-9]{11})[`']?/gi,
    (_match, dataElementId) => `field "${formatDataElementReference(dataElementId, labelMap)}"`,
  );
  formatted = formatted.replace(
    /Attribute: `([a-zA-Z0-9]{11})`/gi,
    (_match, attributeId) => `Attribute "${formatDataElementReference(attributeId, labelMap)}"`,
  );
  formatted = formatted.replace(
    /for ([a-zA-Z0-9]{11}) TrackedEntityAttribute/gi,
    (_match, attributeId) => `for ${formatDataElementReference(attributeId, labelMap)}`,
  );
  formatted = formatted.replace(
    /for attribute ([a-zA-Z0-9]{11})/gi,
    (_match, attributeId) => `for attribute ${formatDataElementReference(attributeId, labelMap)}`,
  );

  return formatted;
}

function collectTrackerValidationMessages(report, labelMap) {
  const messages = [];

  const pushUnique = (message) => {
    const formatted = formatTrackerValidationMessage(message, labelMap);
    if (formatted && !messages.includes(formatted)) {
      messages.push(formatted);
    }
  };

  (report?.validationReport?.errorReports || []).forEach((entry) => {
    pushUnique(entry?.message);
  });

  const typeReportMap = report?.bundleReport?.typeReportMap || {};
  Object.values(typeReportMap).forEach((typeReport) => {
    (typeReport?.objectReports || []).forEach((objectReport) => {
      (objectReport?.errorReports || []).forEach((entry) => {
        pushUnique(entry?.message);
      });
    });
  });

  return messages;
}

function isTrackerImportTerminalStatus(status) {
  const normalized = String(status || "").toUpperCase();
  return ["OK", "COMPLETED", "ERROR", "WARNING"].includes(normalized);
}

function hasTrackerImportReportPayload(report) {
  return Boolean(report?.validationReport || report?.bundleReport || report?.stats);
}

function extractTrackerImportJobId(responseData, responseHeaders) {
  const candidates = [];
  const location =
    responseHeaders?.location ||
    responseHeaders?.Location ||
    responseData?.response?.location;

  if (location) {
    const locationMatch = String(location).match(/tracker\/jobs\/([a-zA-Z0-9]{11})/i);
    if (locationMatch?.[1]) {
      candidates.push(locationMatch[1]);
    }
  }

  [responseData?.response?.id, responseData?.id, responseData?.response?.uid].forEach((candidate) => {
    if (candidate) {
      candidates.push(candidate);
    }
  });

  return candidates.find((candidate) => isValidTrackerUid(candidate)) || null;
}

function getTimelineEventValidationErrors(timelineSheets, programMetadata) {
  const errors = [];

  (timelineSheets || []).forEach((sheet) => {
    const programStage = resolveProgramStageFromSheetName(sheet.sheetName, programMetadata);
    if (!programStage) return;

    const fieldsById = new Map(
      getTimelineStageFields(programStage).map((field) => [field.name, field]),
    );
    const fieldColumns = getTimelineFieldColumns(sheet, programStage);

    (sheet.rows || []).forEach((row, rowIndex) => {
      if (isTimelineHeaderRow(row)) return;

      const rawTeiId = normalizeBulkloadCellValue(row?.[1]);
      const teiId = isValidTrackerUid(rawTeiId) ? String(rawTeiId).trim() : "";

      if (!teiId) {
        errors.push(
          `${sheet.sheetName}, row ${rowIndex + LEGACY_TEMPLATE_HEADER_ROW_INDEX + 2}: TEI Id is required for timeline events.`,
        );
        return;
      }

      if (isTimelineUpdateRow(row)) {
        return;
      }

      const missingRequired = fieldColumns
        .map(({ dataElementId, columnIndex }) => {
          const field = fieldsById.get(dataElementId);
          if (!field?.required) {
            return null;
          }

          const normalizedValue = normalizeTimelineFieldValue(row?.[columnIndex], field);
          if (normalizedValue === null || normalizedValue === undefined || normalizedValue === "") {
            return field.label;
          }

          return null;
        })
        .filter(Boolean);

      if (missingRequired.length > 0) {
        errors.push(
          `${sheet.sheetName}, row ${rowIndex + LEGACY_TEMPLATE_HEADER_ROW_INDEX + 2}: Missing required column${
            missingRequired.length === 1 ? "" : "s"
          } -> ${missingRequired.join(", ")}`,
        );
      }
    });
  });

  return errors;
}

function getTimelineEventsPayloadValidationErrors(events, programMetadata) {
  const errors = [];
  const programStagesById = new Map(
    getSortedProgramStages(programMetadata).map((stage) => [stage.id, stage]),
  );

  (events || []).forEach((event, eventIndex) => {
    const programStage = programStagesById.get(event?.programStage);
    if (!programStage) return;

    const presentFieldIds = new Set(
      (event?.dataValues || []).map((dataValue) => dataValue?.dataElement).filter(Boolean),
    );
    const missingRequired = getTimelineStageFields(programStage)
      .filter((field) => field.required && !presentFieldIds.has(field.name))
      .map((field) => field.label);

    if (missingRequired.length > 0) {
      const eventRef = event?.event ? `event ${event.event}` : `timeline event ${eventIndex + 1}`;
      errors.push(
        `${eventRef}: Missing required field${missingRequired.length === 1 ? "" : "s"} -> ${missingRequired.join(", ")}`,
      );
    }
  });

  return errors;
}

async function hydrateTimelineEventsFromServer(draftEvents, programMetadata) {
  const programStagesById = new Map(
    getSortedProgramStages(programMetadata).map((stage) => [stage.id, stage]),
  );

  await Promise.all(
    (draftEvents || []).map(async (draft) => {
      if (!draft?.event) return;

      const programStage = programStagesById.get(draft.programStage);
      if (!programStage) return;

      const presentFieldIds = new Set(
        (draft.dataValues || []).map((dataValue) => dataValue?.dataElement).filter(Boolean),
      );
      // Preserve template-excluded file/image values and any missing required non-file fields.
      const idsToHydrate = new Set(
        [
          ...getTimelineStageFields(programStage)
            .filter((field) => field.required)
            .map((field) => field.name),
          ...getProgramStageEventFields(programStage)
            .filter((field) => !field.isSectionHeader && isBulkloadFileOrImageField(field))
            .map((field) => field.name),
        ].filter((fieldId) => fieldId && !presentFieldIds.has(fieldId)),
      );

      if (idsToHydrate.size === 0) return;

      try {
        const response = await axios.get(`/tracker/events/${draft.event}`, {
          params: { fields: "event,dataValues[dataElement,value]" },
        });
        const serverValues = response?.data?.dataValues || [];
        const serverMap = new Map(
          serverValues.map((dataValue) => [getDataElementId(dataValue), dataValue?.value]),
        );

        idsToHydrate.forEach((fieldId) => {
          const serverValue = serverMap.get(fieldId);
          if (serverValue === null || serverValue === undefined || serverValue === "") {
            return;
          }

          draft.dataValues.push({
            dataElement: fieldId,
            value: String(serverValue),
          });
        });
      } catch (error) {
        console.warn(`Unable to hydrate event ${draft.event} before import`, error);
      }
    }),
  );
}

function getTimelineStageFields(programStage) {
  return getProgramStageEventFields(programStage).filter(
    (field) => !field.isSectionHeader && !isBulkloadFileOrImageField(field),
  );
}

function buildTimelineEventRow(event, teiId, programStage) {
  const fields = getTimelineStageFields(programStage);
  const dataValueMap = new Map();

  (Array.isArray(event?.dataValues) ? event.dataValues : []).forEach((dataValue) => {
    const dataElementId = getDataElementId(dataValue);
    if (dataElementId) {
      dataValueMap.set(dataElementId, dataValue?.value);
    }
  });

  const row = [
    normalizeTemplateCellValue(event?.event || ""),
    normalizeTemplateCellValue(teiId || event?.trackedEntity || ""),
    "default",
    normalizeTemplateCellValue(toDatePart(event?.occurredAt || event?.eventDate || event?.scheduledAt)),
  ];

  fields.forEach((field) => {
    row.push(normalizeTemplateCellValue(dataValueMap.get(field.name)));
  });

  return row;
}

function collectRecordEvents(record, programId) {
  const teiId =
    record?.trackedEntityInstance ||
    record?.trackedEntityInstance?.id ||
    record?.trackedEntity?.id ||
    record?.trackedEntity ||
    record?.id ||
    "";
  const events = [];

  (Array.isArray(record?.enrollments) ? record.enrollments : []).forEach((enrollment) => {
    const enrollmentProgramId = enrollment?.program?.id || enrollment?.program || "";

    if (programId && enrollmentProgramId && enrollmentProgramId !== programId) {
      return;
    }

    (Array.isArray(enrollment?.events) ? enrollment.events : []).forEach((event) => {
      events.push({ event, teiId, enrollment });
    });
  });

  (Array.isArray(record?.events) ? record.events : []).forEach((event) => {
    events.push({ event, teiId, enrollment: null });
  });

  return events;
}

function getRecordEnrollmentForProgram(record, programId) {
  return (
    (Array.isArray(record?.enrollments) ? record.enrollments : []).find((entry) => {
      const enrollmentProgramId = entry?.program?.id || entry?.program || "";
      return !programId || !enrollmentProgramId || enrollmentProgramId === programId;
    }) ||
    record?.enrollments?.[0] ||
    null
  );
}

function getRecordRegistrationDate(record, programId) {
  const createdAt = toDatePart(record?.createdAt || record?.created);
  if (createdAt) {
    return createdAt;
  }

  const enrollment = getRecordEnrollmentForProgram(record, programId);
  return toDatePart(enrollment?.enrolledAt);
}

function sortRecordsByRegistrationDate(records, programId) {
  return [...(records || [])].sort((leftRecord, rightRecord) => {
    const leftDate = getRecordRegistrationDate(leftRecord, programId);
    const rightDate = getRecordRegistrationDate(rightRecord, programId);

    if (leftDate === rightDate) {
      return 0;
    }

    if (!leftDate) return 1;
    if (!rightDate) return -1;

    return rightDate.localeCompare(leftDate);
  });
}

function isDateWithinInclusiveRange(dateValue, startDate, endDate) {
  const datePart = toDatePart(dateValue);
  if (!datePart || !startDate || !endDate) {
    return false;
  }

  return datePart >= startDate && datePart <= endDate;
}

function filterRecordsByRegistrationDate(records, programId, startDate, endDate) {
  return (records || []).filter((record) =>
    isDateWithinInclusiveRange(getRecordRegistrationDate(record, programId), startDate, endDate),
  );
}

function buildTimelineSheetsData(allRecords, programMetadata, programId) {
  const stages = getSortedProgramStages(programMetadata);
  const rowsByStageId = new Map(stages.map((stage) => [stage.id, []]));

  allRecords.forEach((record) => {
    collectRecordEvents(record, programId).forEach(({ event, teiId }) => {
      const stageId = event?.programStage?.id || event?.programStage || "";
      if (!stageId || !rowsByStageId.has(stageId)) {
        return;
      }

      const stage = stages.find((candidate) => candidate.id === stageId);
      if (!stage) {
        return;
      }

      rowsByStageId.get(stageId).push(buildTimelineEventRow(event, teiId, stage));
    });
  });

  const usedNames = new Set();
  return stages.map((stage, stageIndex) => ({
    name: buildTimelineSheetName(stageIndex, stage, usedNames),
    stage,
    headers: getTimelineSheetHeaders(stage),
    rows: rowsByStageId.get(stage.id) || [],
  }));
}

function appendTimelineDataElementMetadata({
  workbook,
  worksheet,
  legendWorksheet,
  metadataWorksheet,
  validationWorksheet,
  programStage,
  metadataRowNumber,
  validationColumnIndex,
}) {
  const fields = getTimelineStageFields(programStage);
  let nextValidationColumnIndex = validationColumnIndex;

  fields.forEach((field, fieldIndex) => {
    const templateHeader = getTimelineSheetHeaders(programStage)[TIMELINE_SYSTEM_COLUMN_COUNT + fieldIndex];
    const optionValues = Array.isArray(field.options)
      ? field.options.map((option) => option.label || option.value).filter(Boolean)
      : field.type === "select"
        ? ["Yes", "No"]
        : [];
    const possibleValues = optionValues.join(", ");
    const metadataType = "programStageDataElements";

    const fieldLabel =
      resolveBulkloadDisplayLabel(field.label, templateHeader, field.name) ||
      field.name ||
      "Field";

    legendWorksheet.addRow([
      fieldLabel,
      field.description || "",
      field.valueType || "TEXT",
      "",
      possibleValues,
    ]);
    metadataWorksheet.addRow([
      field.name,
      metadataType,
      templateHeader,
      field.valueType || "TEXT",
      "",
      possibleValues,
    ]);

    // Plain header text — formula/named-range headers previously resolved as #VALUE! in Excel.
    worksheet.getCell(
      LEGACY_TEMPLATE_HEADER_ROW_INDEX + 1,
      TIMELINE_SYSTEM_COLUMN_COUNT + fieldIndex + 1,
    ).value = templateHeader;
    metadataRowNumber += 1;

    if (optionValues.length === 0) {
      return;
    }

    const validationColumn = validationWorksheet.getColumn(nextValidationColumnIndex + 1);
    validationColumn.values = [field.name, ...optionValues];
    validationColumn.width = Math.min(
      Math.max(...optionValues.map((value) => String(value).length), fieldLabel.length) + 2,
      60,
    );
    const validationDefinedName = `_options_${field.name}`;
    const validationRange = `'Validation'!$${validationColumn.letter}$2:$${
      validationColumn.letter
    }$${optionValues.length + 1}`;
    workbook.definedNames.add(validationRange, validationDefinedName);

    const targetColumn = worksheet.getColumn(TIMELINE_SYSTEM_COLUMN_COUNT + fieldIndex + 1);
    worksheet.dataValidations.add(`${targetColumn.letter}6:${targetColumn.letter}1048576`, {
      type: "list",
      allowBlank: field.required !== true,
      formulae: [validationDefinedName],
      showErrorMessage: true,
      errorStyle: "error",
      errorTitle: "Invalid value",
      error: `Select a value provided for ${field.label}.`,
    });
    nextValidationColumnIndex += 1;
  });

  return {
    metadataRowNumber,
    validationColumnIndex: nextValidationColumnIndex,
  };
}

function addTimelineWorksheet({
  workbook,
  worksheet,
  legendWorksheet,
  metadataWorksheet,
  validationWorksheet,
  timelineSheet,
  metadataRowNumber,
  validationColumnIndex,
}) {
  configureLegacyWorksheetShell(worksheet);
  applyLegacyWorksheetHeaders(
    worksheet,
    timelineSheet.headers,
    TIMELINE_SYSTEM_COLUMN_COUNT,
  );

  const nextState = appendTimelineDataElementMetadata({
    workbook,
    worksheet,
    legendWorksheet,
    metadataWorksheet,
    validationWorksheet,
    programStage: timelineSheet.stage,
    metadataRowNumber,
    validationColumnIndex,
  });

  if (Array.isArray(timelineSheet.rows) && timelineSheet.rows.length > 0) {
    timelineSheet.rows.forEach((row) => {
      if (Array.isArray(row)) {
        worksheet.addRow(row);
      }
    });
  }

  return nextState;
}

function parseLegacyMatrixWorksheet(worksheet, headerRowIndex, dataStartIndex) {
  const matrixRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "-" });

  if (!Array.isArray(matrixRows) || matrixRows.length <= dataStartIndex) {
    return null;
  }

  return buildMatrixParseCandidate(matrixRows, headerRowIndex, dataStartIndex);
}

function normalizeTimelineFieldValue(rawValue, field) {
  const normalizedValue = normalizeTrackerName(rawValue);
  if (!normalizedValue || normalizedValue === "-") {
    return null;
  }

  if (!Array.isArray(field?.options) || field.options.length === 0) {
    if (field?.type === "select") {
      if (normalizedValue === "yes" || normalizedValue === "true") return "true";
      if (normalizedValue === "no" || normalizedValue === "false") return "false";
    }
    return rawValue;
  }

  const matchingOption = field.options.find((option) =>
    [option.label, option.value].some(
      (candidate) => normalizeTrackerName(candidate) === normalizedValue,
    ),
  );

  return matchingOption?.value || rawValue;
}

function extractEnrollmentIdFromRecord(record, programId) {
  const enrollment = getRecordEnrollmentForProgram(record, programId);
  return enrollment?.enrollment || enrollment?.enrollmentId || enrollment?.id || "";
}

async function resolveEnrollmentIdsByTrackedEntity(teiIds, programId, orgUnitId) {
  const uniqueTeiIds = Array.from(
    new Set((teiIds || []).map((teiId) => String(teiId || "").trim()).filter(isValidTrackerUid)),
  );
  const enrollmentByTeiId = new Map();

  if (uniqueTeiIds.length === 0 || !programId) {
    return enrollmentByTeiId;
  }

  const chunkSize = 40;
  for (let offset = 0; offset < uniqueTeiIds.length; offset += chunkSize) {
    const chunk = uniqueTeiIds.slice(offset, offset + chunkSize);

    await Promise.all(
      chunk.map(async (teiId) => {
        try {
          const fetchEnrollmentId = async (withOrgUnitFilter) => {
            const response = await axios.get(`/tracker/trackedEntities/${teiId}`, {
              params: {
                program: programId,
                fields: "trackedEntity,enrollments[enrollment,program,enrolledAt,status]",
                ...(withOrgUnitFilter && orgUnitId ? { orgUnit: orgUnitId } : {}),
              },
            });
            return extractEnrollmentIdFromRecord(response?.data, programId);
          };

          let enrollmentId = await fetchEnrollmentId(true);
          if (!enrollmentId && orgUnitId) {
            enrollmentId = await fetchEnrollmentId(false);
          }

          if (enrollmentId && isValidTrackerUid(enrollmentId)) {
            enrollmentByTeiId.set(teiId, enrollmentId);
          }
        } catch (error) {
          console.warn(`Unable to resolve enrollment for tracked entity ${teiId}`, error);
        }
      }),
    );
  }

  return enrollmentByTeiId;
}

async function attachEnrollmentIdsToTrackedEntities(trackedEntities, programId, orgUnitId) {
  const entities = Array.isArray(trackedEntities) ? trackedEntities : [];
  const teiIds = entities
    .map((entity) => entity?.trackedEntity)
    .filter((teiId) => isValidTrackerUid(teiId));

  if (teiIds.length === 0 || !programId) {
    return entities;
  }

  const enrollmentByTeiId = await resolveEnrollmentIdsByTrackedEntity(teiIds, programId, orgUnitId);

  return entities.map((entity) => {
    const enrollmentId = enrollmentByTeiId.get(entity?.trackedEntity);

    if (!enrollmentId || !Array.isArray(entity?.enrollments) || entity.enrollments.length === 0) {
      return entity;
    }

    return {
      ...entity,
      enrollments: entity.enrollments.map((enrollment, index) =>
        index === 0 ? { ...enrollment, enrollment: enrollmentId } : enrollment,
      ),
    };
  });
}

async function buildTimelineEventsFromSheets(timelineSheets, selectedTracker, orgUnitId, programId) {
  const programMetadata = selectedTracker?.program;
  const draftEvents = [];

  (timelineSheets || []).forEach((sheet) => {
    const programStage = resolveProgramStageFromSheetName(sheet.sheetName, programMetadata);
    if (!programStage?.id || !Array.isArray(sheet.rows) || sheet.rows.length === 0) {
      return;
    }

    const fieldsById = new Map(
      getTimelineStageFields(programStage).map((field) => [field.name, field]),
    );
    const fieldColumns = getTimelineFieldColumns(sheet, programStage);

    sheet.rows.forEach((row) => {
      if (isTimelineHeaderRow(row)) {
        return;
      }

      const rawEventId = normalizeBulkloadCellValue(row?.[0]);
      const rawTeiId = normalizeBulkloadCellValue(row?.[1]);
      const eventId = isValidTrackerUid(rawEventId) ? String(rawEventId).trim() : "";
      const teiId = isValidTrackerUid(rawTeiId) ? String(rawTeiId).trim() : "";
      const occurredAt = normalizeTrackerImportDate(row?.[3]);
      const dataValues = fieldColumns
        .map(({ dataElementId, columnIndex }) => {
          const field = fieldsById.get(dataElementId) || { name: dataElementId };
          const rawValue = row?.[columnIndex];
          const normalizedValue = normalizeTimelineFieldValue(rawValue, field);
          if (normalizedValue === null || normalizedValue === undefined || normalizedValue === "") {
            return null;
          }

          return {
            dataElement: dataElementId,
            value: String(normalizedValue),
          };
        })
        .filter(Boolean);

      if (!eventId && !teiId && dataValues.length === 0) {
        return;
      }

      if (!eventId && !teiId) {
        return;
      }

      draftEvents.push({
        ...(eventId ? { event: eventId } : {}),
        program: programId,
        programStage: programStage.id,
        orgUnit: orgUnitId,
        ...(teiId ? { trackedEntity: teiId } : {}),
        occurredAt,
        status: "COMPLETED",
        dataValues,
      });
    });
  });

  const enrollmentByTeiId = await resolveEnrollmentIdsByTrackedEntity(
    draftEvents.map((event) => event.trackedEntity).filter(Boolean),
    programId,
    orgUnitId,
  );

  const eventsWithEnrollment = draftEvents.map((event) => {
    const enrollmentId = enrollmentByTeiId.get(event.trackedEntity);
    return {
      ...event,
      ...(enrollmentId ? { enrollment: enrollmentId } : {}),
    };
  });

  await hydrateTimelineEventsFromServer(eventsWithEnrollment, programMetadata);

  return eventsWithEnrollment;
}

export async function downloadTrackerBulkloadTemplate(
  selectedTracker,
  columnConfig,
  dataRows = [],
  timelineSheets = [],
) {
  const excelModule = await import("exceljs");
  const ExcelJS = excelModule.default || excelModule;
  const trackerLabel = selectedTracker?.label || "Tracker";
  const programMetadata = await resolveProgramMetadataForBulkload(selectedTracker);
  const resolvedTracker = programMetadata
    ? { ...selectedTracker, program: programMetadata }
    : selectedTracker;
  const attributes = getLegacyTrackerTemplateAttributes(resolvedTracker, columnConfig);
  const headers = getLegacyTrackerTemplateHeaders(resolvedTracker, columnConfig);
  const systemColumnCount = 5;
  // Blank "Download Template" stays TEI-only. Timeline sheets are included only when
  // "Download Template with Data" (or another caller) passes timelineSheets.
  const resolvedTimelineSheets = Array.isArray(timelineSheets) ? timelineSheets : [];
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(EXPECTED_BULKLOAD_SHEET_NAME);
  const timelineWorksheetEntries = resolvedTimelineSheets.map((timelineSheet) => ({
    timelineSheet,
    worksheet: workbook.addWorksheet(
      sanitizeExcelWorksheetName(timelineSheet.name, "Timeline"),
    ),
  }));
  const legendWorksheet = workbook.addWorksheet("Legend");
  const validationWorksheet = workbook.addWorksheet("Validation", { state: "veryHidden" });
  const metadataWorksheet = workbook.addWorksheet("Metadata");

  workbook.creator = "DDDP";
  workbook.created = new Date();
  configureLegacyWorksheetShell(worksheet);
  applyLegacyWorksheetHeaders(worksheet, headers, systemColumnCount);
  worksheet.getColumn(3).hidden = true;

  legendWorksheet.addRow(["Name", "Description", "Value Type", "Option Set", "Possible Values"]);
  metadataWorksheet.addRow([
    "Identifier",
    "Type",
    "Name",
    "Value Type",
    "Option Set",
    "Possible Values",
  ]);
  [legendWorksheet, metadataWorksheet].forEach((supportWorksheet) => {
    supportWorksheet.getRow(1).font = { bold: true };
    supportWorksheet.getRow(1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD9EAF7" },
    };
    supportWorksheet.columns.forEach((column) => {
      column.width = 24;
    });
  });

  attributes.forEach((entry, attributeIndex) => {
    const attribute = entry.trackedEntityAttribute;
    const attributeLabel = getPreferredBulkloadAttributeLabel(entry, columnConfig);
    const templateHeader = headers[systemColumnCount + attributeIndex];
    const optionSet = attribute?.optionSet;
    const optionValues = Array.isArray(optionSet?.options)
      ? optionSet.options
          .map((option) => option?.displayName || option?.name || option?.code || option?.id)
          .filter(Boolean)
      : /^(BOOLEAN|TRUE_ONLY)$/i.test(attribute?.valueType || "")
        ? attribute.valueType === "TRUE_ONLY"
          ? ["Yes"]
          : ["Yes", "No"]
        : [];
    const possibleValues = optionValues.join(", ");

    legendWorksheet.addRow([
      attributeLabel,
      attribute.description || "",
      attribute.valueType || "TEXT",
      optionSet?.displayName || "",
      possibleValues,
    ]);
    metadataWorksheet.addRow([
      attribute.id,
      "trackedEntityAttributes",
      templateHeader,
      attribute.valueType || "TEXT",
      optionSet?.displayName || "",
      possibleValues,
    ]);
    // Plain header text — formula/named-range headers previously resolved as #VALUE! in Excel.
    worksheet.getCell(
      LEGACY_TEMPLATE_HEADER_ROW_INDEX + 1,
      systemColumnCount + attributeIndex + 1,
    ).value = templateHeader;

    if (optionValues.length === 0) return;

    const validationColumn = validationWorksheet.getColumn(attributeIndex + 1);
    validationColumn.values = [attribute.id, ...optionValues];
    validationColumn.width = Math.min(
      Math.max(...optionValues.map((value) => String(value).length), attributeLabel.length) + 2,
      60,
    );
    const validationDefinedName = `_options_${attribute.id}`;
    const validationRange = `'Validation'!$${validationColumn.letter}$2:$${
      validationColumn.letter
    }$${optionValues.length + 1}`;
    workbook.definedNames.add(validationRange, validationDefinedName);

    const targetColumn = worksheet.getColumn(systemColumnCount + attributeIndex + 1);
    worksheet.dataValidations.add(`${targetColumn.letter}6:${targetColumn.letter}1048576`, {
      type: "list",
      allowBlank: entry?.mandatory !== true,
      formulae: [validationDefinedName],
      showErrorMessage: true,
      errorStyle: "error",
      errorTitle: "Invalid value",
      error: `Select a value provided for ${attributeLabel}.`,
    });
  });

  if (Array.isArray(dataRows) && dataRows.length > 0) {
    dataRows.forEach((row) => {
      if (Array.isArray(row)) {
        worksheet.addRow(row);
      }
    });
  }

  let metadataRowNumber = attributes.length + 2;
  let validationColumnIndex = attributes.length;

  timelineWorksheetEntries.forEach(({ timelineSheet, worksheet: timelineWorksheet }) => {
    const nextState = addTimelineWorksheet({
      workbook,
      worksheet: timelineWorksheet,
      legendWorksheet,
      metadataWorksheet,
      validationWorksheet,
      timelineSheet,
      metadataRowNumber,
      validationColumnIndex,
    });
    metadataRowNumber = nextState.metadataRowNumber;
    validationColumnIndex = nextState.validationColumnIndex;
  });

  const workbookBuffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([workbookBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const downloadUrl = URL.createObjectURL(blob);
  const downloadLink = document.createElement("a");
  downloadLink.href = downloadUrl;
  downloadLink.download = `${trackerLabel} - Bulkload Template.xlsx`;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  downloadLink.remove();
  window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
}

function toDatePart(value) {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (!str || str === "-") return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  return str.split("T")[0];
}

function normalizeTemplateCellValue(value) {
  if (value === null || value === undefined || value === "" || value === "-") return "-";
  if (value === true) return "Yes";
  if (value === false) return "No";
  return value;
}

function extractTrackerResponseRecords(data) {
  return data?.instances || data?.trackedEntities || data?.trackedEntityInstances || [];
}

function extractTrackerResponsePageCount(data, pageSizeFallback = 200) {
  const explicitPageCount = data?.pager?.pageCount ?? data?.pager?.pageCount;
  if (typeof explicitPageCount === "number" && explicitPageCount > 0) return explicitPageCount;

  const total = data?.pager?.total ?? data?.total;
  const pageSize = data?.pager?.pageSize ?? data?.pageSize ?? pageSizeFallback;
  if (typeof total === "number" && typeof pageSize === "number" && pageSize > 0) {
    return Math.max(1, Math.ceil(total / pageSize));
  }
  return 1;
}

export async function downloadTrackerBulkloadTemplateWithData(
  selectedTracker,
  columnConfig,
  orgUnitId,
  startDate,
  endDate,
) {
  const programId =
    typeof selectedTracker === "object"
      ? selectedTracker?.value || selectedTracker?.program?.id
      : "";

  if (!programId || !orgUnitId) {
    throw new Error("Choose a district or sub-area before downloading this template.");
  }

  const sd = toDatePart(startDate);
  const ed = toDatePart(endDate);

  if (!sd || !ed) {
    throw new Error("Select a valid start date and end date.");
  }

  const programMetadata = await resolveProgramMetadataForBulkload(selectedTracker);
  const resolvedTracker = programMetadata
    ? { ...selectedTracker, program: programMetadata }
    : selectedTracker;

  // Same fields used for tracker listing in TrackersModuleAdapter.
  const TRACKER_RELATIONSHIP_RECORD_FIELDS =
    "trackedEntity,trackedEntityInstance,createdAt,inactive,geometry,orgUnit,relationships[relationship,relationshipType,relationshipName,relationshipTypeName,from[trackedEntityInstance[trackedEntityInstance],trackedEntity[trackedEntity],enrollment[enrollment],event[event]],to[trackedEntityInstance[trackedEntityInstance],trackedEntity[trackedEntity],enrollment[enrollment],event[event]]],enrollments[enrollment,program,status,enrolledAt,occurredAt,events[event,eventDate,occurredAt,scheduledAt,status,programStage,program,orgUnit,dataValues[dataElement,displayName,value]]],attributes[attribute,displayName,value]";

  const attributes = getLegacyTrackerTemplateAttributes(resolvedTracker, columnConfig);
  const headers = getLegacyTrackerTemplateHeaders(resolvedTracker, columnConfig);

  const fetchPage = async (page) => {
    const query =
      `orgUnit=${orgUnitId}` +
      `&program=${programId}` +
      `&pageSize=200` +
      `&totalPages=true` +
      `&order=createdAt:desc` +
      `&fields=${TRACKER_RELATIONSHIP_RECORD_FIELDS}` +
      `&page=${page}`;

    const response = await axios.get(`/tracker/trackedEntities?${query}`);
    return response?.data ?? {};
  };

  const first = await fetchPage(1);
  const pageCount = extractTrackerResponsePageCount(first, 200);

  let allRecords = extractTrackerResponseRecords(first);
  if (pageCount > 1) {
    const pageRequests = Array.from({ length: pageCount - 1 }, (_, i) => fetchPage(i + 2));
    const responses = await Promise.all(pageRequests);
    const nextRecords = responses.flatMap((r) => extractTrackerResponseRecords(r));
    allRecords = [...allRecords, ...nextRecords];
  }

  allRecords = sortRecordsByRegistrationDate(
    filterRecordsByRegistrationDate(allRecords, programId, sd, ed),
    programId,
  );

  if (allRecords.length === 0) {
    throw new Error(
      `No records found with registration dates between ${sd} and ${ed}. Try widening the date range.`,
    );
  }

  const dataRows = [];

  allRecords.forEach((record) => {
    const teiId =
      record?.trackedEntityInstance ||
      record?.trackedEntityInstance?.id ||
      record?.trackedEntity?.id ||
      record?.trackedEntity ||
      record?.id ||
      "";

    const enrollment = getRecordEnrollmentForProgram(record, programId);
    const enrolledAt = getRecordRegistrationDate(record, programId);
    const incidentDate = toDatePart(enrollment?.occurredAt || enrolledAt);
    const attrs = Array.isArray(record?.attributes) ? record.attributes : [];
    const row = [];

    row[0] = normalizeTemplateCellValue(teiId);
    row[1] = normalizeTemplateCellValue(orgUnitId);
    row[2] = "-";
    row[3] = normalizeTemplateCellValue(enrolledAt || "");
    row[4] = normalizeTemplateCellValue(incidentDate || "");

    attributes.forEach((attrEntry) => {
      const expectedAttrId = attrEntry?.trackedEntityAttribute?.id;
      const matched = attrs.find((attribute) => {
        const candidateId =
          attribute?.attribute || attribute?.id || attribute?.trackedEntityAttribute?.id;
        return expectedAttrId && String(candidateId) === String(expectedAttrId);
      });
      row.push(normalizeTemplateCellValue(matched?.value));
    });

    while (row.length < headers.length) row.push("-");
    if (row.length > headers.length) row.length = headers.length;

    if (row.every((cell) => cell === "-" || cell === "")) return;

    dataRows.push(row);
  });

  const timelineSheets = buildTimelineSheetsData(
    allRecords,
    programMetadata,
    programId,
  );

  await downloadTrackerBulkloadTemplate(
    resolvedTracker,
    columnConfig,
    dataRows,
    timelineSheets,
  );
}

export function getTrackerBulkloadConfig(selectedTracker) {
  const trackerId =
    typeof selectedTracker === "object" ? selectedTracker?.value || selectedTracker?.program?.id : "";
  const trackerLabel =
    typeof selectedTracker === "string" ? selectedTracker : selectedTracker?.label;
  const normalizedLabel = normalizeTrackerName(trackerLabel);

  return (
    TRACKER_BULKLOAD_CONFIGS.find((config) => {
      const idMatches = Array.isArray(config.programIds) && config.programIds.includes(trackerId);
      const nameMatches = (config.trackerNames || []).some((name) =>
        normalizedLabel.includes(normalizeTrackerName(name)),
      );

      return idMatches || nameMatches;
    }) || null
  );
}

function hasNewTeiBulkloadRows(hydratedRows, systemColumnIndexes) {
  const teiIdIndex = systemColumnIndexes?.teiId ?? 0;

  return (hydratedRows || []).some((row) => {
    const teiId = normalizeBulkloadCellValue(row?.[teiIdIndex]);
    if (isValidTrackerUid(teiId)) {
      return false;
    }

    return (row || []).some((cell, index) => {
      if (index <= teiIdIndex) {
        return false;
      }

      const normalized = normalizeBulkloadCellValue(cell);
      return normalized && normalized !== "-";
    });
  });
}

function shouldOmitTrackedEntitiesFromBulkloadImport(hydratedRows, events, systemColumnIndexes) {
  if (!Array.isArray(events) || events.length === 0) {
    return false;
  }

  if (hasNewTeiBulkloadRows(hydratedRows, systemColumnIndexes)) {
    return false;
  }

  const teiIdIndex = systemColumnIndexes?.teiId ?? 0;
  const hasExistingTeiRows = (hydratedRows || []).some((row) => {
    const teiId = normalizeBulkloadCellValue(row?.[teiIdIndex]);
    return isValidTrackerUid(teiId);
  });

  return hasExistingTeiRows;
}

function getMandatoryFieldMessages(fields, trackedEntities, { hasTimelineEvents = false } = {}) {
  if (!Array.isArray(fields) || fields.length === 0) {
    return [];
  }

  if (!Array.isArray(trackedEntities) || trackedEntities.length === 0) {
    return hasTimelineEvents ? [] : ["No tracker rows were prepared for import."];
  }

  return trackedEntities.reduce((messages, entity, index) => {
    const missingFields = fields.reduce((missing, field) => {
      const match = Array.isArray(entity?.attributes)
        ? entity.attributes.find((attribute) => attribute?.attribute === field.attribute)
        : null;
      const value = match?.value;

      if (value === null || value === undefined || value === "") {
        missing.push(field.description || field.attribute);
      }

      return missing;
    }, []);

    if (missingFields.length > 0) {
      messages.push(`Row ${index + 1}: Missing fields -> ${missingFields.join(", ")}`);
    }

    return messages;
  }, []);
}

function parseBulkloadCoordinatePair(first, second) {
  const a = Number(first);
  const b = Number(second);

  if (!Number.isFinite(a) || !Number.isFinite(b)) {
    return null;
  }

  if (a < -180 || a > 180 || b < -180 || b > 180) {
    return null;
  }

  const aLooksLikeLatitude = Math.abs(a) >= 4 && Math.abs(a) <= 15 && a > 0;
  const bLooksLikeLongitude = b <= 2 && b >= -4;
  const bLooksLikeLatitude = Math.abs(b) >= 4 && Math.abs(b) <= 15 && b > 0;
  const aLooksLikeLongitude = a <= 2 && a >= -4;

  if (aLooksLikeLatitude && bLooksLikeLongitude) {
    return `[${b},${a}]`;
  }

  if (aLooksLikeLongitude && bLooksLikeLatitude) {
    return `[${a},${b}]`;
  }

  if (Math.abs(a) <= 90 && Math.abs(b) > 90) {
    return `[${b},${a}]`;
  }

  return `[${a},${b}]`;
}

const BULKLOAD_KNOWN_COORDINATE_ATTRIBUTE_IDS = new Set([
  "cPGCeyMceZl",
  "ocZLeifIbuv",
  "PPjSFYtywHl",
  "MJCxNfLfrIy",
]);

function isBulkloadCoordinateAttribute(attributeId, attribute) {
  if (attributeId && BULKLOAD_KNOWN_COORDINATE_ATTRIBUTE_IDS.has(attributeId)) {
    return true;
  }

  return String(attribute?.valueType || "").toUpperCase() === "COORDINATE";
}

function normalizeBulkloadCoordinateValue(rawValue) {
  if (rawValue === null || rawValue === undefined) {
    return null;
  }

  const stringValue = String(rawValue).trim();
  if (!stringValue || stringValue === "-" || normalizeTrackerName(stringValue) === "none") {
    return null;
  }

  const patterns = [
    /^\[\s*([+-]?\d+(?:\.\d+)?)\s*,\s*([+-]?\d+(?:\.\d+)?)\s*\]$/,
    /^\(\s*([+-]?\d+(?:\.\d+)?)\s*,\s*([+-]?\d+(?:\.\d+)?)\s*\)$/,
    /^([+-]?\d+(?:\.\d+)?)\s*,\s*([+-]?\d+(?:\.\d+)?)$/,
    /^([+-]?\d+(?:\.\d+)?)\s+([+-]?\d+(?:\.\d+)?)$/,
    /^POINT\s*\(\s*([+-]?\d+(?:\.\d+)?)\s+([+-]?\d+(?:\.\d+)?)\s*\)$/i,
  ];

  for (const pattern of patterns) {
    const match = stringValue.match(pattern);
    if (match) {
      return parseBulkloadCoordinatePair(match[1], match[2]);
    }
  }

  return null;
}

function getBulkloadCoordinateAttributeIds(importAttributes) {
  const coordinateAttributeIds = new Set(BULKLOAD_KNOWN_COORDINATE_ATTRIBUTE_IDS);

  (importAttributes || []).forEach((entry) => {
    const attributeId = entry?.trackedEntityAttribute?.id;
    if (attributeId && isBulkloadCoordinateAttribute(attributeId, entry?.trackedEntityAttribute)) {
      coordinateAttributeIds.add(attributeId);
    }
  });

  return coordinateAttributeIds;
}

function sanitizeBulkloadAttributeValues(attributes, coordinateAttributeIds) {
  return (attributes || [])
    .map((entry) => {
      const attributeId = entry?.attribute;
      const value = entry?.value;

      if (!attributeId || !coordinateAttributeIds.has(attributeId)) {
        return entry;
      }

      const normalizedCoordinate = normalizeBulkloadCoordinateValue(value);
      if (!normalizedCoordinate) {
        return null;
      }

      return {
        attribute: attributeId,
        value: normalizedCoordinate,
      };
    })
    .filter(Boolean);
}

function sanitizeTrackedEntityBulkloadAttributes(trackedEntities, coordinateAttributeIds) {
  return (trackedEntities || []).map((entity) => ({
    ...entity,
    attributes: sanitizeBulkloadAttributeValues(entity?.attributes, coordinateAttributeIds),
    enrollments: (entity?.enrollments || []).map((enrollment) => ({
      ...enrollment,
      attributes: sanitizeBulkloadAttributeValues(enrollment?.attributes, coordinateAttributeIds),
    })),
  }));
}

function getInvalidCoordinateMessages(
  rows,
  attributeColumns,
  coordinateAttributeIds,
  importAttributes,
  fieldLabelMap,
) {
  if (!Array.isArray(rows) || rows.length === 0 || coordinateAttributeIds.size === 0) {
    return [];
  }

  const labelByAttributeId = new Map(
    (importAttributes || []).map((entry) => [
      entry?.trackedEntityAttribute?.id,
      getPreferredBulkloadAttributeLabel(entry),
    ]),
  );

  return rows.reduce((messages, row, rowIndex) => {
    (attributeColumns || []).forEach(({ attributeId, columnIndex }) => {
      if (!coordinateAttributeIds.has(attributeId)) {
        return;
      }

      const rawValue = row?.[columnIndex];
      if (isBlankBulkloadAttributeValue(rawValue)) {
        return;
      }

      if (normalizeBulkloadCoordinateValue(rawValue)) {
        return;
      }

      const label =
        fieldLabelMap?.[attributeId] ||
        labelByAttributeId.get(attributeId) ||
        attributeId;
      messages.push(
        `Row ${rowIndex + 1}: "${label}" must be a coordinate like [-0.187,5.604] or 5.604,-0.187.`,
      );
    });

    return messages;
  }, []);
}

const MEETINGS_ATTRIBUTE_ALIASES = {
  j0kNoCtC8pj: {
    "extra ordinary meeting": "Special Meeting",
    "extraordinary meeting": "Special Meeting",
    "emergency meeting": "Special Meeting",
  },
  GlYGlfsNFQZ: {
    "extra ordinary meeting": "Extra ordinary meeting",
    "extraordinary meeting": "Extra ordinary meeting",
    "emergency meeting": "Emergency Meeting",
    "rcc special meeting": "RCC Special Meeting",
    "expanded rcc meeting": "Expanded RCC Meeting",
    "expanded rpcu meeting": "Expanded RPCU Meeting",
    "regional audit committee": "Regional Audit Committee",
    "regional budget committee": "Regional Budget Committee",
  },
  kghpIZgHFsT: {
    "general assembly": "GA",
    "management meeting": "Management Meetings",
    "public relations and complaints committee(prcc)": "PRCC",
    "emergency meeting": "Other Meeting",
    "extra ordinary meeting": "Statutory Committee Meetings",
    "ordinary meeting": "Statutory Committee Meetings",
  },
  rSKzHSBlOyB: {
    "presiding member": "PM",
    convener: "C",
  },
  AiPyh2A2DZ8: {
    none: "",
  },
};

function normalizeAttributeImportValue(rawValue, attribute, attributeId = "") {
  if (rawValue === null || rawValue === undefined || rawValue === "" || rawValue === "-") {
    return null;
  }

  const valueType = String(attribute?.valueType || "").toUpperCase();
  let stringValue = String(rawValue).trim();

  if (typeof rawValue === "number" && Number.isFinite(rawValue)) {
    if (valueType === "DATE" || valueType === "DATETIME") {
      const excelDate = normalizeExcelSerialDate(rawValue);
      if (excelDate) {
        stringValue = valueType === "DATE" ? toDatePart(excelDate) : excelDate;
      }
    }
  }

  const options = attribute?.optionSet?.options;

  if (Array.isArray(options) && options.length > 0) {
    const normalizedName = normalizeTrackerName(stringValue);
    const aliasValue = MEETINGS_ATTRIBUTE_ALIASES[attributeId]?.[normalizedName];
    const searchValue = aliasValue || stringValue;
      const matchingOption = options.find((option) =>
        [option?.displayName, option?.name, option?.code, option?.id].some(
        (candidate) => normalizeTrackerName(candidate) === normalizeTrackerName(searchValue),
      ),
    );

    return (
      matchingOption?.code ||
      matchingOption?.name ||
      matchingOption?.displayName ||
      matchingOption?.id ||
      stringValue
    );
  }

  if (valueType === "BOOLEAN" || valueType === "TRUE_ONLY") {
    const normalized = normalizeTrackerName(stringValue);
    if (normalized === "yes" || normalized === "true") return true;
    if (normalized === "no" || normalized === "false") return false;
    if (normalized === "none") return null;
    if (/^\d+$/.test(stringValue)) return null;
    return null;
  }

  if (valueType === "COORDINATE" || isBulkloadCoordinateAttribute(attributeId, attribute)) {
    return normalizeBulkloadCoordinateValue(stringValue);
  }

  if (/INTEGER|NUMBER|ZERO_OR_POSITIVE|POSITIVE/.test(valueType)) {
    const parsed = Number.parseInt(String(stringValue).replace(/,/g, ""), 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  }

  if (valueType === "DATE") {
    return toDatePart(stringValue) || null;
  }

  if (valueType === "DATETIME") {
    return normalizeTrackerImportDate(stringValue, null) || stringValue;
  }

  if (isBulkloadCoordinateAttribute(attributeId, attribute)) {
    return normalizeBulkloadCoordinateValue(stringValue);
  }

  return stringValue;
}

const BULKLOAD_AUTO_GENERATED_ATTRIBUTE_IDS = new Set(["s9DLL3i1DoG"]);
const BULKLOAD_UNIQUE_ATTRIBUTE_IDS = new Set([bulkloadUtils.BULKLOAD_TIN_ATTRIBUTE_ID]);

function generateBulkloadAttributeValue(attributeId) {
  if (attributeId === "s9DLL3i1DoG") {
    return bulkloadUtils.generateProjectId();
  }

  if (attributeId === bulkloadUtils.BULKLOAD_TIN_ATTRIBUTE_ID) {
    return bulkloadUtils.generateBulkloadTin();
  }

  return null;
}

function resolveBulkloadUniqueAttributeValue(attributeId, rawValue, { hasTeiId } = {}) {
  if (attributeId !== bulkloadUtils.BULKLOAD_TIN_ATTRIBUTE_ID) {
    return undefined;
  }

  const normalizedValue = normalizeBulkloadCellValue(rawValue);
  const stringValue =
    normalizedValue === null || normalizedValue === undefined ? "" : String(normalizedValue).trim();

  if (hasTeiId) {
    if (!stringValue || bulkloadUtils.isBulkloadPlaceholderTin(stringValue)) {
      return null;
    }
    return stringValue;
  }

  if (!stringValue || bulkloadUtils.isBulkloadPlaceholderTin(stringValue)) {
    return bulkloadUtils.generateBulkloadTin();
  }

  return stringValue;
}

function assignUniqueBulkloadTinValues(rows, attributeColumns, systemColumnIndexes) {
  const column = (attributeColumns || []).find(
    (entry) => entry.attributeId === bulkloadUtils.BULKLOAD_TIN_ATTRIBUTE_ID,
  );

  if (!column) {
    return rows;
  }

  const teiIdIndex = systemColumnIndexes?.teiId ?? 0;
  const usedValues = new Set();

  return (rows || []).map((row) => {
    const nextRow = Array.isArray(row) ? [...row] : [];
    const rawTeiId = normalizeBulkloadCellValue(nextRow?.[teiIdIndex]);
    const hasTeiId = isValidTrackerUid(rawTeiId);

    if (hasTeiId) {
      return nextRow;
    }

    let value = resolveBulkloadUniqueAttributeValue(
      bulkloadUtils.BULKLOAD_TIN_ATTRIBUTE_ID,
      nextRow?.[column.columnIndex],
      { hasTeiId: false },
    );
    let valueKey = String(value || "").trim().toLowerCase();

    while (!valueKey || usedValues.has(valueKey)) {
      value = bulkloadUtils.generateBulkloadTin();
      valueKey = String(value).trim().toLowerCase();
    }

    nextRow[column.columnIndex] = value;
    usedValues.add(valueKey);
    return nextRow;
  });
}

function isBlankBulkloadAttributeValue(value) {
  return value === null || value === undefined || value === "-" || value === "";
}

function resolveBulkloadAttributeValue(attributeId, rawValue, { hasTeiId } = {}) {
  if (BULKLOAD_UNIQUE_ATTRIBUTE_IDS.has(attributeId)) {
    const uniqueValue = resolveBulkloadUniqueAttributeValue(attributeId, rawValue, { hasTeiId });
    if (uniqueValue !== undefined) {
      return uniqueValue;
    }
  }

  if (!isBlankBulkloadAttributeValue(rawValue)) {
    return rawValue;
  }

  if (hasTeiId) {
    return null;
  }

  return generateBulkloadAttributeValue(attributeId);
}

function getBulkloadGeneratedAttributeIds(importAttributes) {
  const generatedAttributeIds = new Set(BULKLOAD_AUTO_GENERATED_ATTRIBUTE_IDS);

  (importAttributes || []).forEach((entry) => {
    const attributeId = entry?.trackedEntityAttribute?.id;
    if (!attributeId) {
      return;
    }

    if (entry?.trackedEntityAttribute?.generated) {
      generatedAttributeIds.add(attributeId);
    }
  });

  return generatedAttributeIds;
}

async function hydrateMissingBulkloadGeneratedAttributes(
  rows,
  attributeColumns,
  systemColumnIndexes,
  generatedAttributeIds,
  programId,
) {
  const teiIdIndex = systemColumnIndexes?.teiId ?? 0;
  const generatedColumns = (attributeColumns || []).filter(({ attributeId }) =>
    generatedAttributeIds.has(attributeId),
  );

  if (generatedColumns.length === 0) {
    return rows;
  }

  const hydratedRows = (rows || []).map((row) => (Array.isArray(row) ? [...row] : []));
  const teiIdsNeedingHydration = new Set();

  hydratedRows.forEach((row) => {
    const rawTeiId = normalizeBulkloadCellValue(row?.[teiIdIndex]);
    const teiId = isValidTrackerUid(rawTeiId) ? String(rawTeiId).trim() : null;

    if (!teiId) {
      return;
    }

    const needsHydration = generatedColumns.some(({ columnIndex }) =>
      isBlankBulkloadAttributeValue(row?.[columnIndex]),
    );

    if (needsHydration) {
      teiIdsNeedingHydration.add(teiId);
    }
  });

  if (teiIdsNeedingHydration.size === 0) {
    return hydratedRows;
  }

  const attributesByTeiId = new Map();

  await Promise.all(
    [...teiIdsNeedingHydration].map(async (teiId) => {
      try {
        const response = await axios.get(`/tracker/trackedEntities/${teiId}`, {
          params: {
            program: programId,
            fields: "attributes[attribute,value]",
          },
        });
        const attributeMap = new Map();

        (Array.isArray(response?.data?.attributes) ? response.data.attributes : []).forEach(
          (attribute) => {
            const attributeId = attribute?.attribute;
            if (attributeId) {
              attributeMap.set(attributeId, attribute?.value);
            }
          },
        );

        attributesByTeiId.set(teiId, attributeMap);
      } catch (error) {
        console.warn(`Unable to hydrate generated attributes for tracked entity ${teiId}`, error);
      }
    }),
  );

  hydratedRows.forEach((row) => {
    const rawTeiId = normalizeBulkloadCellValue(row?.[teiIdIndex]);
    const teiId = isValidTrackerUid(rawTeiId) ? String(rawTeiId).trim() : null;
    const attributeMap = teiId ? attributesByTeiId.get(teiId) : null;

    if (!attributeMap) {
      return;
    }

    generatedColumns.forEach(({ attributeId, columnIndex }) => {
      if (!isBlankBulkloadAttributeValue(row?.[columnIndex])) {
        return;
      }

      const existingValue = attributeMap.get(attributeId);
      if (!isBlankBulkloadAttributeValue(existingValue)) {
        row[columnIndex] = existingValue;
      }
    });
  });

  return hydratedRows;
}

function getMandatoryBulkloadAttributeIds(programMetadata) {
  const mandatoryAttributeIds = new Set();

  (programMetadata?.trackedEntityType?.trackedEntityTypeAttributes || []).forEach((entry) => {
    const attributeId = entry?.trackedEntityAttribute?.id;
    if (attributeId && entry?.mandatory) {
      mandatoryAttributeIds.add(attributeId);
    }
  });

  (programMetadata?.programTrackedEntityAttributes || []).forEach((entry) => {
    const attributeId = entry?.trackedEntityAttribute?.id;
    if (attributeId && entry?.mandatory) {
      mandatoryAttributeIds.add(attributeId);
    }
  });

  return mandatoryAttributeIds;
}

function buildGenericTrackerBulkloadPayload({
  rows,
  attributeColumns,
  systemColumnIndexes,
  orgUnitId,
  programId,
  trackedEntityTypeId,
  generatedAttributeIds = new Set(),
  coordinateAttributeIds = new Set(),
  mandatoryAttributeIds = new Set(),
}) {
  const teiIdIndex = systemColumnIndexes?.teiId ?? 0;
  const orgUnitIndex = systemColumnIndexes?.orgUnit ?? 1;
  const enrollmentDateIndex = systemColumnIndexes?.enrollmentDate ?? 3;
  const incidentDateIndex = systemColumnIndexes?.incidentDate ?? 4;

  const trackedEntities = (rows || []).map((row) => {
    const rawTeiId = normalizeBulkloadCellValue(row?.[teiIdIndex]);
    const teiId = isValidTrackerUid(rawTeiId) ? String(rawTeiId).trim() : null;
    const rowOrgUnit = normalizeBulkloadCellValue(row?.[orgUnitIndex]);
    const resolvedOrgUnit = rowOrgUnit && rowOrgUnit !== "-" ? rowOrgUnit : orgUnitId;
    const enrolledAt = normalizeTrackerImportDate(row?.[enrollmentDateIndex]);
    const occurredAt = normalizeTrackerImportDate(row?.[incidentDateIndex], enrolledAt);

    const attributes = (attributeColumns || [])
      .map(({ attributeId, columnIndex }) => {
        const value = resolveBulkloadAttributeValue(attributeId, row?.[columnIndex], {
          hasTeiId: Boolean(teiId),
        });
        const isMandatory = mandatoryAttributeIds.has(attributeId);

        if (isBlankBulkloadAttributeValue(value)) {
          // Mandatory TET/program attributes must still be declared or DHIS2 rejects the TEI.
          if (isMandatory) {
            return {
              attribute: attributeId,
              value: "",
            };
          }
          return null;
        }

        if (coordinateAttributeIds.has(attributeId)) {
          const normalizedCoordinate = normalizeBulkloadCoordinateValue(value);
          if (!normalizedCoordinate) {
            return isMandatory
              ? {
                  attribute: attributeId,
                  value: "",
                }
              : null;
          }

          return {
            attribute: attributeId,
            value: normalizedCoordinate,
          };
        }

        return {
          attribute: attributeId,
          value,
        };
      })
      .filter(Boolean);

    const enrollmentAttributes = (attributeColumns || [])
      .map(({ attributeId, columnIndex }) => {
        const resolvedValue = resolveBulkloadAttributeValue(attributeId, row?.[columnIndex], {
          hasTeiId: Boolean(teiId),
        });
        const cellValue = row?.[columnIndex];
        const value = !isBlankBulkloadAttributeValue(resolvedValue)
          ? resolvedValue
          : !isBlankBulkloadAttributeValue(cellValue)
            ? cellValue
            : "";

        if (generatedAttributeIds.has(attributeId) && isBlankBulkloadAttributeValue(value)) {
          return null;
        }

        if (coordinateAttributeIds.has(attributeId)) {
          const normalizedCoordinate = normalizeBulkloadCoordinateValue(value);
          if (!normalizedCoordinate) {
            return null;
          }

          return {
            attribute: attributeId,
            value: normalizedCoordinate,
          };
        }

        return {
          attribute: attributeId,
          value,
        };
      })
      .filter(Boolean);

    const entity = {
      orgUnit: resolvedOrgUnit,
      trackedEntityType: trackedEntityTypeId,
      attributes,
      enrollments: [
        {
          program: programId,
          orgUnit: resolvedOrgUnit,
          enrolledAt,
          occurredAt,
          status: "ACTIVE",
          attributes: enrollmentAttributes,
        },
      ],
    };

    if (teiId) {
      entity.trackedEntity = teiId;
    }

    return entity;
  });

  return { trackedEntities };
}

async function hydrateMandatoryTrackedEntityAttributesFromServer(
  trackedEntities,
  mandatoryAttributeIds,
) {
  if (!Array.isArray(trackedEntities) || trackedEntities.length === 0 || !mandatoryAttributeIds?.size) {
    return trackedEntities;
  }

  await Promise.all(
    trackedEntities.map(async (entity) => {
      const teiId = entity?.trackedEntity;
      if (!isValidTrackerUid(teiId)) {
        return;
      }

      const attributes = Array.isArray(entity.attributes) ? entity.attributes : [];
      const attributeById = new Map(
        attributes
          .filter((entry) => entry?.attribute)
          .map((entry) => [entry.attribute, entry]),
      );
      const idsNeedingHydration = [...mandatoryAttributeIds].filter((attributeId) => {
        const current = attributeById.get(attributeId);
        return !current || isBlankBulkloadAttributeValue(current.value);
      });

      if (idsNeedingHydration.length === 0) {
        return;
      }

      try {
        const response = await axios.get(`/tracker/trackedEntities/${teiId}`, {
          params: { fields: "trackedEntity,attributes[attribute,value]" },
        });
        const serverAttributes = Array.isArray(response?.data?.attributes)
          ? response.data.attributes
          : [];
        const serverById = new Map(
          serverAttributes
            .map((entry) => [
              entry?.attribute || entry?.trackedEntityAttribute?.id,
              entry?.value,
            ])
            .filter(([attributeId]) => attributeId),
        );

        idsNeedingHydration.forEach((attributeId) => {
          const serverValue = serverById.get(attributeId);
          if (isBlankBulkloadAttributeValue(serverValue)) {
            return;
          }

          const existing = attributeById.get(attributeId);
          if (existing) {
            existing.value = String(serverValue);
            return;
          }

          const nextEntry = {
            attribute: attributeId,
            value: String(serverValue),
          };
          attributes.push(nextEntry);
          attributeById.set(attributeId, nextEntry);
        });

        entity.attributes = attributes;
      } catch (error) {
        console.warn(`Unable to hydrate mandatory attributes for TEI ${teiId}`, error);
      }
    }),
  );

  return trackedEntities;
}

function normalizeTrackerBulkloadRowValues(rows, attributeMetaList, attributeColumns, systemColumnIndexes) {
  const attributeMetaById = new Map();

  (attributeMetaList || []).forEach((entry) => {
    const attributeId = entry?.trackedEntityAttribute?.id;
    if (attributeId) {
      attributeMetaById.set(attributeId, entry.trackedEntityAttribute);
    }
  });

  const enrollmentDateIndex = systemColumnIndexes?.enrollmentDate ?? 3;
  const incidentDateIndex = systemColumnIndexes?.incidentDate ?? 4;

  return (rows || []).map((row) => {
    const normalizedRow = Array.isArray(row) ? [...row] : [];

    (attributeColumns || []).forEach(({ attributeId, columnIndex }) => {
      const attribute = attributeMetaById.get(attributeId);
      const normalizedValue = normalizeAttributeImportValue(
        normalizedRow[columnIndex],
        attribute,
        attributeId,
      );

      if (normalizedValue === null) {
        normalizedRow[columnIndex] = "-";
        return;
      }

      normalizedRow[columnIndex] = normalizedValue;
    });

    const enrollmentDate = normalizeAttributeImportValue(normalizedRow[enrollmentDateIndex], {
      valueType: "DATETIME",
    });
    if (enrollmentDate) {
      normalizedRow[enrollmentDateIndex] = enrollmentDate;
    }

    const incidentDate = normalizeAttributeImportValue(normalizedRow[incidentDateIndex], {
      valueType: "DATETIME",
    });
    if (incidentDate) {
      normalizedRow[incidentDateIndex] = incidentDate;
    }

    return normalizedRow;
  });
}

function buildAttributeColumnsFromMetadata(importAttributes, systemColumnIndexes = null) {
  const firstAttributeColumn = (systemColumnIndexes?.incidentDate ?? 4) + 1;

  return importAttributes
    .map((entry, attributeIndex) => ({
      attributeId: entry?.trackedEntityAttribute?.id,
      columnIndex: firstAttributeColumn + attributeIndex,
    }))
    .filter((column) => column.attributeId);
}

function normalizeTrackerBulkloadOptionValues(rows, selectedTracker) {
  const importAttributes = getLegacyTrackerTemplateAttributes(selectedTracker);
  const attributeColumns = buildAttributeColumnsFromMetadata(importAttributes);

  return normalizeTrackerBulkloadRowValues(rows, importAttributes, attributeColumns, null);
}

export async function buildTrackerBulkloadPayload({
  headers,
  attributeIds,
  attributeColumns = [],
  systemColumnIndexes = null,
  rows,
  timelineSheets = [],
  selectedTracker,
  columnConfig,
  orgUnitId,
}) {
  const config = getTrackerBulkloadConfig(selectedTracker);

  if (!config) {
    throw new Error("Bulkload is not ready yet for this tracker.");
  }

  if (!orgUnitId) {
    throw new Error("Choose a district or sub-area before importing data.");
  }

  const programId = selectedTracker?.value || selectedTracker?.program?.id;
  const trackedEntityTypeId = selectedTracker?.program?.trackedEntityType?.id;

  if (!programId || !trackedEntityTypeId) {
    throw new Error("Tracker details are still loading. Refresh and try again.");
  }

  const programMetadata = await resolveProgramMetadataForBulkload(selectedTracker);
  const resolvedTracker = programMetadata
    ? { ...selectedTracker, program: programMetadata }
    : selectedTracker;

  validateTrackerBulkloadHeaders(
    headers,
    attributeIds,
    resolvedTracker,
    columnConfig,
    attributeColumns,
  );

  const fieldLabelMap = buildDataElementLabelMap(
    programMetadata || resolvedTracker?.program,
    columnConfig,
  );

  const importAttributes = getLegacyTrackerTemplateAttributes(resolvedTracker, columnConfig);
  const resolvedSystemColumnIndexes =
    systemColumnIndexes ||
    resolveBulkloadSystemColumnIndexes(
      getLegacyTrackerTemplateHeaders(resolvedTracker, columnConfig),
    );
  const resolvedAttributeColumns =
    Array.isArray(attributeColumns) && attributeColumns.length > 0
      ? attributeColumns
      : buildAttributeColumnsFromMetadata(importAttributes, resolvedSystemColumnIndexes);

  const normalizedRows = normalizeTrackerBulkloadRowValues(
    rows,
    importAttributes,
    resolvedAttributeColumns,
    resolvedSystemColumnIndexes,
  );

  const tinPreparedRows = assignUniqueBulkloadTinValues(
    normalizedRows,
    resolvedAttributeColumns,
    resolvedSystemColumnIndexes,
  );

  const generatedAttributeIds = getBulkloadGeneratedAttributeIds(importAttributes);
  const coordinateAttributeIds = getBulkloadCoordinateAttributeIds(importAttributes);
  const mandatoryAttributeIds = getMandatoryBulkloadAttributeIds(
    programMetadata || resolvedTracker?.program,
  );
  const hydratedRows = await hydrateMissingBulkloadGeneratedAttributes(
    tinPreparedRows,
    resolvedAttributeColumns,
    resolvedSystemColumnIndexes,
    generatedAttributeIds,
    programId,
  );

  let payload = await addGeneratedTrackerAttributeValues(
    stripGeneratedTrackerAttributes(
      normalizeTrackerImportPayload(
        resolvedAttributeColumns.length > 0
          ? buildGenericTrackerBulkloadPayload({
              rows: hydratedRows,
              attributeColumns: resolvedAttributeColumns,
              systemColumnIndexes: resolvedSystemColumnIndexes,
              orgUnitId,
              programId,
              trackedEntityTypeId,
              generatedAttributeIds,
              coordinateAttributeIds,
              mandatoryAttributeIds,
            })
          : config.buildPayload(hydratedRows, orgUnitId, programId, trackedEntityTypeId),
      ),
      resolvedTracker,
    ),
    resolvedTracker,
  );

  if (Array.isArray(payload?.trackedEntities) && payload.trackedEntities.length > 0) {
    payload = {
      ...payload,
      trackedEntities: sanitizeTrackedEntityBulkloadAttributes(
        await hydrateMandatoryTrackedEntityAttributesFromServer(
          await attachEnrollmentIdsToTrackedEntities(
            payload.trackedEntities,
            programId,
            orgUnitId,
          ),
          mandatoryAttributeIds,
        ),
        coordinateAttributeIds,
      ),
    };
  }

  let trackedEntities = Array.isArray(payload?.trackedEntities) ? payload.trackedEntities : [];
  const timelineValidationErrors = getTimelineEventValidationErrors(
    timelineSheets,
    programMetadata || resolvedTracker?.program,
  );
  const events = await buildTimelineEventsFromSheets(
    timelineSheets,
    resolvedTracker,
    orgUnitId,
    programId,
  );
  const timelinePayloadValidationErrors = getTimelineEventsPayloadValidationErrors(
    events,
    programMetadata || resolvedTracker?.program,
  );
  const omitTrackedEntities = shouldOmitTrackedEntitiesFromBulkloadImport(
    hydratedRows,
    events,
    resolvedSystemColumnIndexes,
  );

  if (omitTrackedEntities) {
    trackedEntities = [];
  }

  if (trackedEntities.length === 0 && events.length === 0) {
    throw new Error("No tracker rows were ready for import.");
  }

  const eventsMissingEnrollment = events.filter((event) => !event?.enrollment);
  if (eventsMissingEnrollment.length > 0) {
    throw new Error(
      `Could not resolve enrollment for ${eventsMissingEnrollment.length} timeline event${
        eventsMissingEnrollment.length === 1 ? "" : "s"
      }. Check that the TEI Id values exist in this tracker and org unit.`,
    );
  }

  const combinedPayload = {
    ...(trackedEntities.length > 0 ? { trackedEntities } : {}),
    ...(events.length > 0 ? { events } : {}),
  };

  const mandatoryFields = bulkloadUtils
    .getMandatoryFieldByTracker(config.mandatoryTrackerKey)
    .filter((field) => !generatedAttributeIds.has(field.attribute));
  const validationErrors = [
    ...(omitTrackedEntities
      ? []
      : getInvalidCoordinateMessages(
          hydratedRows,
          resolvedAttributeColumns,
          coordinateAttributeIds,
          importAttributes,
          fieldLabelMap,
        )),
    ...getMandatoryFieldMessages(mandatoryFields, trackedEntities, {
      hasTimelineEvents: events.length > 0,
    }),
    ...timelineValidationErrors,
    ...timelinePayloadValidationErrors,
  ];

  return {
    config,
    payload: combinedPayload,
    trackedEntities,
    events,
    validationErrors,
    fieldLabelMap,
  };
}

export async function startTrackerBulkloadImport(
  payload,
  { importStrategy = "CREATE_AND_UPDATE", asyncImport = false } = {},
) {
  try {
    const asyncQuery = asyncImport ? "async=true&" : "";
    const response = await axios.post(
      `/tracker?${asyncQuery}importStrategy=${importStrategy}&skipRuleEngine=true&skipAudit=true`,
      payload,
    );
    const responseData = response?.data ?? {};

    if (!asyncImport || hasTrackerImportReportPayload(responseData)) {
      return {
        jobId: extractTrackerImportJobId(responseData, response?.headers),
        response: responseData,
        report: responseData,
      };
    }

    const jobId = extractTrackerImportJobId(responseData, response?.headers);

    if (!jobId) {
      throw new Error("The import was submitted, but no tracker job reference was returned.");
    }

    return {
      jobId,
      response: responseData,
      report: null,
    };
  } catch (error) {
    throw new Error(getTrackerBulkloadRequestError(error));
  }
}

export function extractTrackerBulkloadJobError(report, fieldLabelMap = null) {
  const validationMessages = collectTrackerValidationMessages(report, fieldLabelMap);
  if (validationMessages.length > 0) {
    const preview = validationMessages.slice(0, 5).join(" ");
    const remaining = validationMessages.length - 5;
    return remaining > 0 ? `${preview} (+${remaining} more)` : preview;
  }

  const statusMessage = report?.message || report?.description || report?.error;
  if (statusMessage && String(statusMessage).includes("Non-unique attribute value")) {
    return String(statusMessage).split("\n")[0].trim();
  }

  if (typeof statusMessage === "string" && statusMessage.trim()) {
    return statusMessage.split("\n")[0].trim();
  }

  return "Bulkload could not be completed. Please review the file and try again.";
}

async function fetchTrackerBulkloadJobReport(jobId) {
  try {
    const response = await axios.get(`/tracker/jobs/${jobId}/report`, {
      params: { reportMode: "FULL" },
    });
    return response?.data ?? {};
  } catch (error) {
    const statusCode = error?.response?.status;
    if (statusCode !== 404) {
      throw error;
    }

    try {
      const statusResponse = await axios.get(`/tracker/jobs/${jobId}`);
      const status = String(statusResponse?.data?.status || "").toUpperCase();

      if (!isTrackerImportTerminalStatus(status)) {
        return null;
      }

      const retryResponse = await axios.get(`/tracker/jobs/${jobId}/report`, {
        params: { reportMode: "FULL" },
      });
      return retryResponse?.data ?? {};
    } catch (statusError) {
      if (statusError?.response?.status === 404) {
        return null;
      }
      throw statusError;
    }
  }
}

function wait(delayMs) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, delayMs);
  });
}

export async function pollTrackerBulkloadJob(
  jobId,
  { intervalMs = 3000, timeoutMs = 180000, onStatus, fieldLabelMap = null } = {},
) {
  if (!isValidTrackerUid(jobId)) {
    throw new Error("The import job reference returned by the server was invalid.");
  }

  const deadline = Date.now() + timeoutMs;
  let consecutiveNotFound = 0;

  while (Date.now() < deadline) {
    try {
      const report = await fetchTrackerBulkloadJobReport(jobId);

      if (!report) {
        consecutiveNotFound += 1;
        if (consecutiveNotFound >= 3) {
          throw new Error(
            `Tracker import job ${jobId} could not be found. The server may have finished the import before the job report was available.`,
          );
        }
        await wait(intervalMs);
        continue;
      }

      consecutiveNotFound = 0;
      const status = String(report?.status || "").toUpperCase();

      onStatus?.(report);

      if (status === "OK" || status === "COMPLETED") {
        return report;
      }

      if (status === "ERROR") {
        throw new Error(extractTrackerBulkloadJobError(report, fieldLabelMap));
      }
    } catch (error) {
      const statusCode = error?.response?.status;
      const responseMessage = error?.response?.data?.message || error?.message || "";

      if (statusCode === 404) {
        consecutiveNotFound += 1;
        if (consecutiveNotFound >= 3) {
          throw new Error(
            `Tracker import job ${jobId} could not be found. ${responseMessage || "Please try the import again."}`,
          );
        }
        await wait(intervalMs);
        continue;
      }

        if (error instanceof Error) {
          throw error;
        }

        throw new Error("Bulkload could not be completed. Please try again.");
    }

    await wait(intervalMs);
  }

  throw new Error("Bulkload is taking longer than expected. Please check again shortly.");
}

export async function runTrackerBulkloadImport(
  payload,
  { importStrategy = "CREATE_AND_UPDATE", onStatus, fieldLabelMap = null, asyncImport = false } = {},
) {
  let report = null;

  try {
    const { jobId, report: immediateReport } = await startTrackerBulkloadImport(payload, {
      importStrategy,
      asyncImport,
    });

    report = hasTrackerImportReportPayload(immediateReport)
      ? immediateReport
      : jobId
        ? await pollTrackerBulkloadJob(jobId, { onStatus, fieldLabelMap })
        : immediateReport;
  } catch (error) {
    const message = error?.message || "";
    const shouldRetrySynchronously =
      !asyncImport &&
      /job .* could not be found|invalid tracker job reference/i.test(message);

    if (!shouldRetrySynchronously) {
      throw error;
    }

    const { report: syncReport } = await startTrackerBulkloadImport(payload, {
      importStrategy,
      asyncImport: false,
    });
    report = syncReport;
  }

  if (!report) {
    throw new Error("Bulkload could not be completed because no import report was returned.");
  }

  const status = String(report?.status || "").toUpperCase();
  if (status === "ERROR") {
    throw new Error(extractTrackerBulkloadJobError(report, fieldLabelMap));
  }

  return report;
}
